import { authenticatedFetch } from '../api/client';
import AuthContext from '../context/AuthContext';
import { canAccessWidget } from '../widgets/permissions';
import CanvasOverlayVisibility, { createOverlayVisibilityStore } from './CanvasOverlayVisibility';
import CanvasViewportBinding, { createCanvasViewportStore } from './CanvasViewportBinding';
import ClusterWaveformWidget from './ClusterWaveformWidget';
import SpikeWaveformGrid from './SpikeWaveformGrid';
import { SOSSA_WIDGETS } from '../widgets/sossaWidgets';
import { isSossaDataset } from '../utils/sossa';
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo
} from 'react';
import ClusterListTable from './ClusterListTable';
import {
  DEFAULT_CLUSTER_GROUPS,
  normalizeClusterGroups,
} from '../utils/clusterGroups';
import SpikeListTable from './SpikeListTable';
import ClusterStatisticsWindow from './ClusterStatisticsWindow';
import SignalViewPanel from './SignalViewPanel';
import DimensionalityReductionPanel from './DimensionalityReductionPanel';
import WaveformSingleChannelView from './WaveformSingleChannelView';
import WaveformNeighboringChannelsView from './WaveformNeighboringChannelsView';
import DockableWidget from './DockableWidget';
import GeneratedWidget, { generatedWidgetData } from './GeneratedWidget';
import { builderRequest } from './WidgetBuilder';
import DashboardAssistant, { canUseDashboardAssistant } from './DashboardAssistant';
import WidgetBank, { WIDGET_DEFINITIONS } from './WidgetBank';
import RightSideMenu from './RightSideMenu';
import {
  STORAGE_KEY,
  CURRENT_VIEW_KEY,
  getScopedStorageKey,
} from './ViewManager';
import './MultiPanelView.css';
import AmplitudeProfileWidget from './AmplitudeProfileWidget';
import ClusterComparisonWidget from './ClusterComparisonWidget';
import CuratorWidget, { normalizeDataset as normalizeCuratorUpload } from './CuratorWidget';
import createSossaExample from '../data/sossaExample';
import RasterPlotWidget from './RasterPlotWidget';
import CorrelogramWidget from './CorrelogramWidget';
import IsiHistogramWidget from './IsiHistogramWidget';
import AmplitudeTimeWidget from './AmplitudeTimeWidget';
import CurationActionsWidget from './CurationActionsWidget';
import AnalysisWorkspaceWidget from './AnalysisWorkspaceWidget';
import SpikeAttributeExplorerWidget from './SpikeAttributeExplorerWidget';
import TemplateGalleryWidget from './TemplateGalleryWidget';
import ClusterMetricScatterWidget from './ClusterMetricScatterWidget';
import FeatureMatrixWidget from './FeatureMatrixWidget';
import TemplateFeaturePairWidget from './TemplateFeaturePairWidget';
import ProbeMapWidget from './ProbeMapWidget';
import TraceHeatmapWidget from './TraceHeatmapWidget';
import SimilarityTableWidget from './SimilarityTableWidget';
import FiringRateTimelineWidget from './FiringRateTimelineWidget';
import CanvasMinimap from './CanvasMinimap';
import CanvasMarkup from './CanvasMarkup';
import apiClient from '../api/client';
import {
  DEFAULT_DISPLAY_SETTINGS,
  ZOOM_MIN, ZOOM_MAX, ZOOM_STEP, sideMenuZoom,
  normalizeDisplaySettings,
  readDisplaySettings,
} from '../utils/displaySettings';
import {
  createDashboardDataFromCuratorDataset,
  createWaveformPcaClusterData,
  normalizeCuratorDatasetTimes,
  reconcileCuratorClusterSelection,
} from '../utils/curatorDataset';
import {
  createDashboardPipelineVariables,
  mergeWidgetInputBindings,
} from '../widgets/dataContracts';
import {
  getWidgetStatesCenteredViewport,
  getViewportCenteredWidgetPosition,
  normalizeCanvasViewport,
  screenToCanvasPoint,
  zoomViewportAtPoint,
} from '../utils/canvasViewport';
import {
  CANVAS_WHEEL_ACTIONS,
  getCanvasWheelAction,
} from '../utils/canvasWheel';
import {
  WIDGET_SELECTION_DRAG_THRESHOLD,
  getWidgetIdsInSelectionRect,
  mergeWidgetSelection,
  normalizeSelectionRect,
  offsetWidgetPositions,
  resolveWidgetSelection,
} from '../utils/canvasSelection';
import {
  clearSessionCache,
  createSessionCacheKey,
  getOrLoadSessionCache,
  getSessionObjectId,
} from '../utils/sessionCache';
import {
  createCurationExport,
  createCurationSession,
  createCurationSource,
  createCuratedDashboardData,
  deriveCurationState,
  mergeCurationClusters,
  redoCuration,
  restoreCurationSession,
  splitCurationSelection,
  undoCuration,
} from '../utils/curationSession';
import { createAnalysisManifest } from '../utils/analysisWorkspace';

const DISPLAY_SETTINGS_STORAGE_KEY = 'spikescope_display_settings:v1';
const WIDGET_BINDINGS_STORAGE_KEY = 'spikescope_widget_input_bindings:v1';
const CANVAS_OVERLAY_IDLE_MS = 3000;
const MINIMAP_VISIBLE_MS = 10000;

const getDefaultChannelSelection = (datasetInfo) => {
  const totalChannels = Math.max(0, Number(datasetInfo?.totalChannels || 0));
  if (!totalChannels) return [];
  if (totalChannels >= 183) return [179, 181, 183];
  return Array.from({ length: Math.min(3, totalChannels) }, (_, index) => index + 1);
};

const CanvasCursorCoordinates = forwardRef(({ ariaLabel }, ref) => {
  const outputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    show: (point) => {
      const output = outputRef.current;
      if (!output) return;
      const text = `x ${point.x} · y ${point.y}`;
      if (output.textContent !== text) output.textContent = text;
      output.hidden = false;
    },
    hide: () => {
      if (outputRef.current) outputRef.current.hidden = true;
    },
  }), []);

  return (
    <output ref={outputRef} hidden className="canvas-cursor-coordinates" aria-label={ariaLabel} />
  );
});

CanvasCursorCoordinates.displayName = 'CanvasCursorCoordinates';

const getPanelPosition = (panel) => {
  const computedStyle = window.getComputedStyle(panel);
  const inlineLeft = parseFloat(panel.style.left);
  const inlineTop = parseFloat(panel.style.top);
  const computedLeft = parseFloat(computedStyle.left);
  const computedTop = parseFloat(computedStyle.top);

  return {
    left: Number.isFinite(computedLeft)
      ? computedLeft
      : Number.isFinite(inlineLeft)
      ? inlineLeft
      : panel.offsetLeft || 0,
    top: Number.isFinite(computedTop)
      ? computedTop
      : Number.isFinite(inlineTop)
      ? inlineTop
      : panel.offsetTop || 0,
  };
};

const DEFAULT_WIDGET_STATES = {
  clusterWaveform: { visible: false, minimized: false, order: 29, position: null, size: null, type: 'clusterWaveform' },
  spikeWaveformGrid: { visible: false, minimized: false, order: 28, position: null, size: null, type: 'spikeWaveformGrid' },
  ...Object.fromEntries(SOSSA_WIDGETS.map((widget) => [widget.id, { visible: false, minimized: false, order: widget.order, position: null, size: null, type: widget.id }])),
  clusterList: { visible: true, minimized: false, order: 1, position: null, size: null, type: 'clusterList' },
  spikeList: { visible: true, minimized: false, order: 2, position: null, size: null, type: 'spikeList' },
  clusterStats: { visible: true, minimized: false, order: 3, position: null, size: null, type: 'clusterStats' },
  signalView: { visible: true, minimized: false, order: 4, position: null, size: null, type: 'signalView' },
  dimReduction: { visible: true, minimized: false, order: 5, position: null, size: null, type: 'dimReduction' },
  waveform: { visible: true, minimized: false, order: 6, position: null, size: null, type: 'waveform' },
  amplitudeProfile: { visible: false, minimized: false, order: 7, position: null, size: null, type: 'amplitudeProfile' },
  clusterComparison: { visible: false, minimized: false, order: 8, position: null, size: null, type: 'clusterComparison' },
  curator: { visible: false, minimized: false, order: 9, position: null, size: null, type: 'curator' },
  rasterPlot: { visible: false, minimized: false, order: 10, position: null, size: null, type: 'rasterPlot' },
  correlogram: { visible: false, minimized: false, order: 11, position: null, size: null, type: 'correlogram' },
  isiHistogram: { visible: false, minimized: false, order: 12, position: null, size: null, type: 'isiHistogram' },
  amplitudeTime: { visible: false, minimized: false, order: 13, position: null, size: null, type: 'amplitudeTime' },
  firingRateTimeline: { visible: false, minimized: false, order: 14, position: null, size: null, type: 'firingRateTimeline' },
  similarityTable: { visible: false, minimized: false, order: 15, position: null, size: null, type: 'similarityTable' },
  probeMap: { visible: false, minimized: false, order: 16, position: null, size: null, type: 'probeMap' },
  traceHeatmap: { visible: false, minimized: false, order: 17, position: null, size: null, type: 'traceHeatmap' },
  featureMatrix: { visible: false, minimized: false, order: 18, position: null, size: null, type: 'featureMatrix' },
  templateFeaturePair: { visible: false, minimized: false, order: 19, position: null, size: null, type: 'templateFeaturePair' },
  templateGallery: { visible: false, minimized: false, order: 20, position: null, size: null, type: 'templateGallery' },
  clusterMetricScatter: { visible: false, minimized: false, order: 21, position: null, size: null, type: 'clusterMetricScatter' },
  spikeAttributeExplorer: { visible: false, minimized: false, order: 22, position: null, size: null, type: 'spikeAttributeExplorer' },
  analysisWorkspace: { visible: false, minimized: false, order: 23, position: null, size: null, type: 'analysisWorkspace' },
  curationActions: { visible: false, minimized: false, order: 24, position: null, size: null, type: 'curationActions' }
};

export const CURATOR_LINKED_WIDGET_IDS = [
  'clusterStats',
  'signalView',
  'waveform',
  'amplitudeProfile',
  'rasterPlot',
  'curationActions',
];

export const revealCuratorLinkedWidgets = (states = {}) => {
  let changed = false;
  const nextStates = { ...states };

  CURATOR_LINKED_WIDGET_IDS.forEach((widgetId) => {
    const currentState = states[widgetId] || DEFAULT_WIDGET_STATES[widgetId];
    if (!currentState || (currentState.visible && !currentState.minimized)) return;

    nextStates[widgetId] = {
      ...currentState,
      visible: true,
      minimized: false,
    };
    changed = true;
  });

  return changed ? nextStates : states;
};

const mergeWidgetStateDefaults = (states = {}) => {
  const merged = Object.fromEntries(Object.entries(DEFAULT_WIDGET_STATES).map(([widgetId, defaults]) => [
    widgetId,
    { ...defaults, ...(states?.[widgetId] || {}) },
  ]));
  Object.entries(states || {}).forEach(([widgetId, state]) => {
    if (!merged[widgetId]) merged[widgetId] = state;
  });
  return merged;
};

export const getHighestWidgetOrder = (states = {}) => (
  Object.values(states).reduce((highestOrder, state) => {
    const order = Number(state?.order);
    return Number.isFinite(order) ? Math.max(highestOrder, order) : highestOrder;
  }, 0)
);

const MultiPanelView = forwardRef(({
  demoMode = false,
  selectedDataset,
  clusteringResults,
  selectedAlgorithm,
  datasetInfo,
  algorithms,
  onAlgorithmChange,
  onRunAlgorithm,
  onStopAlgorithm,
  isRunningAlgorithm,
  pipelineJob,
  pipelineStatus,
  pipelineMessage,
  pipelineError,
  onOpenParameters,
  customPipelines = [],
  isLoadingCustomPipelines = false,
  customPipelineError = null,
  onAddCustomPipeline,
  onDeleteCustomPipeline,
  canManageCustomPipelines = false,
  savedViews,
  savedCurrentViewId,
  onPersistViews,
  layoutStorageScope,
  demoClusterPlotData = [],
  demoSpikeTable = [],
  demoClusterStats = [],
  demoWaveforms = {},
  demoSignalData = null
}, ref) => {
  const auth = React.useContext(AuthContext);
  const widgetUser = auth?.user;
  const [generatedWidgets, setGeneratedWidgets] = useState([]);
  useEffect(() => {
    setGeneratedWidgets([]);
    if (!canUseDashboardAssistant(widgetUser) || demoMode) return undefined;
    const controller = new AbortController();
    builderRequest('/widgets?canvas=1', { signal: controller.signal }).then(result => {
      const published = result.widgets.filter(w => w.published);
      setGeneratedWidgets(published);
      setWidgetStates(previous => {
        const next = { ...previous };
        published.forEach((w, i) => { if (!next[w.id]) next[w.id] = {
          visible: true, minimized: false, position: { top: 100 + i * 35, left: 160 + i * 35 },
          size: { width: 560, height: 380 }, order: 100 + i,
        }; });
        return next;
      });
    }).catch(() => {}); // The builder shows actionable load errors when opened.
    return () => controller.abort();
  }, [widgetUser?.id, widgetUser?.email, widgetUser?.role, widgetUser?.is_active, demoMode]);
  const containerRef = useRef(null);
  const canvasGridRef = useRef(null);

  const [clusters, setClusters] = useState([]);
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [spikes, setSpikes] = useState([]);
  const [selectedSpike, setSelectedSpike] = useState(null);
  const [clusterStats, setClusterStats] = useState({});
  const [clusterData, setClusterData] = useState(null);
  const [clusterWaveforms, setClusterWaveforms] = useState({});
  const [highlightedSpikes, setHighlightedSpikes] = useState([]);
  const [curationSpikeSelection, setCurationSpikeSelection] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState(() => getDefaultChannelSelection(datasetInfo));
  const [focusedTimeRange, setFocusedTimeRange] = useState(null);
  const [clusterAnnotations, setClusterAnnotations] = useState({});
  const [clusterGroupNames, setClusterGroupNames] = useState(DEFAULT_CLUSTER_GROUPS);
  const [visibleClusterOrder, setVisibleClusterOrder] = useState([]);
  const [visibleClusterFilterActive, setVisibleClusterFilterActive] = useState(false);
  const [curatorDataset, setCuratorDataset] = useState(null);
  const lastClusterSourceRef = useRef({ algorithm: selectedAlgorithm, results: clusteringResults });
  const [curationSource, setCurationSource] = useState(null);
  const [curationSession, setCurationSession] = useState(null);
  const [curationActionError, setCurationActionError] = useState('');
  const [curationRecoveryMessage, setCurationRecoveryMessage] = useState('');
  const curationBaseStateRef = useRef(null);
  const curationSourceRef = useRef(null);
  curationSourceRef.current = curationSource;
  const [waveformViewMode, setWaveformViewMode] = useState('single');
  const [displaySettings, setDisplaySettings] = useState(() => (
    readDisplaySettings(window.localStorage, DISPLAY_SETTINGS_STORAGE_KEY)
  ));
  const [viewportStore] = useState(() => createCanvasViewportStore({ x: 0, y: 0, zoom: displaySettings.scale }));
  const canvasOffset = viewportStore.getSnapshot();
  const canvasViewportRef = useRef(canvasOffset);
  const displayScaleRef = useRef(displaySettings.scale);
  displayScaleRef.current = displaySettings.scale;
  const setCanvasOffset = useCallback((offset) => {
    const viewport = { ...canvasViewportRef.current, ...offset };
    canvasViewportRef.current = viewport;
    // Translate existing DOM immediately; Plotly data and layout stay untouched.
    if (dashboardCanvasRef.current) {
      dashboardCanvasRef.current.style.transform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`;
    }
    if (canvasGridRef.current) {
      const step = 24 * viewport.zoom;
      canvasGridRef.current.style.transform = `translate(${viewport.x % step}px, ${viewport.y % step}px)`;
    }
    viewportStore.set(viewport);
  }, [viewportStore]);
  const [isCanvasPanning, setIsCanvasPanning] = useState(false);
  const [isCanvasSelecting, setIsCanvasSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const [selectedWidgetIds, setSelectedWidgetIds] = useState([]);
  const [groupDraggingWidgetIds, setGroupDraggingWidgetIds] = useState([]);
  const [isRightSideMenuOpen, setIsRightSideMenuOpen] = useState(false);
  const canvasPanRef = useRef(null);
  const canvasSelectionRef = useRef(null);
  const cursorCoordinatesRef = useRef(null);
  const selectedWidgetIdsRef = useRef([]);
  const widgetGroupDragRef = useRef(null);
  const sideMenuWasOpenRef = useRef(false);
  const dashboardCanvasRef = useRef(null);
  const zoomIndicatorTimerRef = useRef(null);
  const minimapTimerRef = useRef(null);
  const [zoomVisibility] = useState(createOverlayVisibilityStore);
  const setIsZoomIndicatorVisible = zoomVisibility.set;
  const [minimapVisibility] = useState(createOverlayVisibilityStore);
  const setIsMinimapVisible = minimapVisibility.set;
  const [canvasGeometry, setCanvasGeometry] = useState({
    width: 0,
    height: 0,
    widgets: [],
  });
  const isCompactCanvas = (
    canvasGeometry.width || (typeof window === 'undefined' ? 0 : window.innerWidth)
  ) <= 640;
  const [widgetLoading, setWidgetLoading] = useState({});
  const sessionCacheInputsRef = useRef({
    clusteringResults,
    curatorDataset,
    selectedAlgorithm,
    selectedDataset,
  });
  const [widgetInputBindings, setWidgetInputBindings] = useState(() => {
    try {
      const saved = localStorage.getItem(WIDGET_BINDINGS_STORAGE_KEY);
      return mergeWidgetInputBindings(saved ? JSON.parse(saved) : {});
    } catch (error) {
      return mergeWidgetInputBindings();
    }
  });

  const annotationStorageKey = useMemo(() => {
    const datasetKey = selectedDataset?.id || selectedDataset?.name || selectedDataset || (demoMode ? 'demo' : 'default');
    return `spikescope_cluster_annotations:${String(datasetKey)}:${selectedAlgorithm || 'none'}`;
  }, [demoMode, selectedAlgorithm, selectedDataset]);
  const clusterGroupStorageKey = `${annotationStorageKey}:groups`;
  const curationStorageKey = `${annotationStorageKey}:curation-session:v1`;

  const [isWidgetBankOpen, setIsWidgetBankOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropPosition, setDropPosition] = useState(null);
  const [currentViewId, setCurrentViewId] = useState(() => (
    savedCurrentViewId ||
    localStorage.getItem(getScopedStorageKey(CURRENT_VIEW_KEY, layoutStorageScope)) ||
    'default'
  ));
  const currentViewIdRef = useRef(currentViewId);
  currentViewIdRef.current = currentViewId;

  const isDefaultView = currentViewId === 'default';
  const dataCacheScope = useMemo(() => createSessionCacheKey('data-scope', [
    selectedDataset?.id || selectedDataset?.name || selectedDataset || (demoMode ? 'demo' : 'default'),
    selectedAlgorithm || 'none',
    getSessionObjectId(clusteringResults),
    getSessionObjectId(curatorDataset),
    datasetInfo?.recordingRevision,
  ]), [
    clusteringResults,
    curatorDataset,
    demoMode,
    selectedAlgorithm,
    selectedDataset,
    datasetInfo?.recordingRevision,
  ]);

  const [widgetStates, setWidgetStates] = useState(() => {
    const localCurrentView =
      localStorage.getItem(getScopedStorageKey(CURRENT_VIEW_KEY, layoutStorageScope)) ||
      'default';
    const initialCurrentView = savedCurrentViewId || localCurrentView;
    const accountViews = Array.isArray(savedViews) ? savedViews : [];
    const accountView = accountViews.find((view) => view.id === initialCurrentView);

    if (accountView?.widgetStates) {
      return mergeWidgetStateDefaults(accountView.widgetStates);
    }

    const localViews = localStorage.getItem(
      getScopedStorageKey(STORAGE_KEY, layoutStorageScope)
    );

    if (initialCurrentView && localViews) {
      try {
        const views = JSON.parse(localViews);
        const currentView = views.find((view) => view.id === initialCurrentView);
        if (currentView?.widgetStates) {
          return mergeWidgetStateDefaults(currentView.widgetStates);
        }
      } catch (e) {
        console.error('Error loading widget states:', e);
      }
    }
    return DEFAULT_WIDGET_STATES;
  });
  const updateSelectedWidgetIds = useCallback((widgetIds) => {
    const nextSelection = mergeWidgetSelection([], widgetIds);
    selectedWidgetIdsRef.current = nextSelection;
    setSelectedWidgetIds(nextSelection);
  }, []);
  const handleWidgetSelect = useCallback((widgetId, { additive = false } = {}) => {
    const nextSelection = resolveWidgetSelection(
      selectedWidgetIdsRef.current,
      widgetId,
      additive
    );
    selectedWidgetIdsRef.current = nextSelection;
    setSelectedWidgetIds(nextSelection);
  }, []);
  useEffect(() => {
    const totalChannels = Math.max(0, Number(datasetInfo?.totalChannels || 0));
    setSelectedChannels((current) => {
      const valid = current.map(Number).filter((channelId, index, values) => (
        Number.isInteger(channelId)
        && channelId >= 1
        && channelId <= totalChannels
        && values.indexOf(channelId) === index
      ));
      return valid.length ? valid : getDefaultChannelSelection(datasetInfo);
    });
  }, [datasetInfo]);

  useEffect(() => {
    const visibleSelection = selectedWidgetIdsRef.current.filter((widgetId) => {
      const state = widgetStates[widgetId];
      return state?.visible;
    });
    if (visibleSelection.length !== selectedWidgetIdsRef.current.length) {
      updateSelectedWidgetIds(visibleSelection);
    }
  }, [updateSelectedWidgetIds, widgetStates]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(annotationStorageKey);
      const annotations = saved ? JSON.parse(saved) : {};
      const layoutGroups = widgetStates.clusterList?.clusterGroups || {};
      Object.entries(layoutGroups).forEach(([clusterId, group]) => {
        annotations[clusterId] = {
          ...(annotations[clusterId] || {}),
          group,
        };
      });
      setClusterAnnotations(annotations);
    } catch (error) {
      console.error('Error loading cluster annotations:', error);
      setClusterAnnotations({});
    }
  }, [annotationStorageKey, widgetStates.clusterList?.clusterGroups]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(clusterGroupStorageKey);
      const layoutGroups = widgetStates.clusterList?.groupNames;
      setClusterGroupNames(normalizeClusterGroups(
        Array.isArray(layoutGroups)
          ? layoutGroups
          : saved
          ? JSON.parse(saved)
          : DEFAULT_CLUSTER_GROUPS
      ));
    } catch (error) {
      console.error('Error loading cluster groups:', error);
      setClusterGroupNames(DEFAULT_CLUSTER_GROUPS);
    }
  }, [clusterGroupStorageKey, widgetStates.clusterList?.groupNames]);

  useEffect(() => {
    const root = document.documentElement;
    const densityScale = { compact: 0.85, standard: 1, comfortable: 1.15 }[displaySettings.density];
    root.style.setProperty('--dashboard-menu-scale', String(displaySettings.scale * densityScale));
    root.style.setProperty('--dashboard-side-menu-scale', String(sideMenuZoom(displaySettings.scale) * densityScale));
    return () => {
      root.style.removeProperty('--dashboard-menu-scale');
      root.style.removeProperty('--dashboard-side-menu-scale');
    };
  }, [displaySettings.scale, displaySettings.density]);

  useEffect(() => {
    localStorage.setItem(DISPLAY_SETTINGS_STORAGE_KEY, JSON.stringify(displaySettings));
  }, [displaySettings]);

  useEffect(() => {
    localStorage.setItem(WIDGET_BINDINGS_STORAGE_KEY, JSON.stringify(widgetInputBindings));
  }, [widgetInputBindings]);

  const handleWidgetLoadingChange = useCallback((widgetId, loading, label = '') => {
    setWidgetLoading((current) => {
      const nextValue = loading ? { loading: true, label } : null;
      if (
        current[widgetId]?.loading === Boolean(nextValue?.loading) &&
        current[widgetId]?.label === (nextValue?.label || '')
      ) {
        return current;
      }
      return { ...current, [widgetId]: nextValue };
    });
  }, []);

  useEffect(() => {
    const previous = sessionCacheInputsRef.current;
    const dataChanged =
      previous.clusteringResults !== clusteringResults ||
      previous.curatorDataset !== curatorDataset ||
      previous.selectedAlgorithm !== selectedAlgorithm ||
      previous.selectedDataset !== selectedDataset;

    sessionCacheInputsRef.current = {
      clusteringResults,
      curatorDataset,
      selectedAlgorithm,
      selectedDataset,
    };

    if (dataChanged) clearSessionCache('widget-data');
  }, [clusteringResults, curatorDataset, selectedAlgorithm, selectedDataset]);

  const revealZoomIndicator = useCallback(() => {
    setIsZoomIndicatorVisible(true);
    if (zoomIndicatorTimerRef.current) {
      clearTimeout(zoomIndicatorTimerRef.current);
    }
    zoomIndicatorTimerRef.current = setTimeout(() => {
      setIsZoomIndicatorVisible(false);
    }, CANVAS_OVERLAY_IDLE_MS);
  }, [setIsZoomIndicatorVisible]);

  const revealMinimap = useCallback(() => {
    setIsMinimapVisible(true);
    if (minimapTimerRef.current) {
      clearTimeout(minimapTimerRef.current);
    }
    minimapTimerRef.current = setTimeout(() => {
      setIsMinimapVisible(false);
      minimapTimerRef.current = null;
    }, MINIMAP_VISIBLE_MS);
  }, [setIsMinimapVisible]);

  const isSideMenuOpen = isWidgetBankOpen || isRightSideMenuOpen;

  useEffect(() => {
    if (isSideMenuOpen) {
      sideMenuWasOpenRef.current = true;
      if (minimapTimerRef.current) {
        clearTimeout(minimapTimerRef.current);
        minimapTimerRef.current = null;
      }
      setIsMinimapVisible(false);
      return;
    }

    if (sideMenuWasOpenRef.current) {
      sideMenuWasOpenRef.current = false;
      revealMinimap();
    }
  }, [isSideMenuOpen, revealMinimap, setIsMinimapVisible]);

  useEffect(() => {
    revealZoomIndicator();
    revealMinimap();

    return () => {
      if (zoomIndicatorTimerRef.current) clearTimeout(zoomIndicatorTimerRef.current);
      if (minimapTimerRef.current) clearTimeout(minimapTimerRef.current);
    };
  }, [revealMinimap, revealZoomIndicator]);

  const handleCursorPositionChange = useCallback((event) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const point = screenToCanvasPoint({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }, canvasViewportRef.current);

    cursorCoordinatesRef.current?.show({
      x: Math.round(point.left),
      y: Math.round(point.top),
    });
  }, []);

  const measureCanvasGeometry = useCallback(() => {
    const container = containerRef.current;
    const canvas = dashboardCanvasRef.current;
    if (!container || !canvas) return;
    if (canvas.querySelector('.dockable-widget.resizing')) return;

    const widgets = Array.from(canvas.querySelectorAll('.panel')).map((panel) => {
      const widget = panel.querySelector('.dockable-widget');
      if (!widget) return null;
      const panelStyle = window.getComputedStyle(panel);
      const widgetRect = widget.getBoundingClientRect();

      return {
        id: widget.dataset.widgetId,
        x: Number.isFinite(panel.offsetLeft)
          ? panel.offsetLeft
          : parseFloat(panelStyle.left) || 0,
        y: Number.isFinite(panel.offsetTop)
          ? panel.offsetTop
          : parseFloat(panelStyle.top) || 0,
        width: widget.offsetWidth || widgetRect.width / displaySettings.scale,
        height: widget.offsetHeight || widgetRect.height / displaySettings.scale,
      };
    }).filter(Boolean);
    const nextGeometry = {
      width: container.clientWidth || container.getBoundingClientRect().width,
      height: container.clientHeight || container.getBoundingClientRect().height,
      widgets,
    };

    setCanvasGeometry((current) => (
      JSON.stringify(current) === JSON.stringify(nextGeometry)
        ? current
        : nextGeometry
    ));
  }, [displaySettings.scale]);

  useEffect(() => {
    measureCanvasGeometry();
    const container = containerRef.current;
    const canvas = dashboardCanvasRef.current;
    if (!container || !canvas) return undefined;

    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(measureCanvasGeometry);
    observer?.observe(container);
    canvas.querySelectorAll('.dockable-widget').forEach((widget) => observer?.observe(widget));
    window.addEventListener('resize', measureCanvasGeometry);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measureCanvasGeometry);
    };
  }, [measureCanvasGeometry, widgetStates]);

  const setCanvasZoom = useCallback((requestedScale, point) => {
    revealZoomIndicator();
    revealMinimap();
    const nextScale = normalizeDisplaySettings({ scale: requestedScale }).scale;
    const rect = containerRef.current?.getBoundingClientRect();
    const anchor = point || {
      x: (rect?.width || 0) / 2,
      y: (rect?.height || 0) / 2,
    };
    const nextView = zoomViewportAtPoint(
      canvasViewportRef.current,
      nextScale,
      anchor
    );

    canvasViewportRef.current = nextView;
    setCanvasOffset({ x: nextView.x, y: nextView.y });
    setDisplaySettings((current) => ({ ...current, scale: nextScale }));
  }, [revealMinimap, revealZoomIndicator]);

  const handleDisplaySettingsChange = useCallback((patch) => {
    if (patch.scale !== undefined) {
      setCanvasZoom(patch.scale);
      return;
    }
    setDisplaySettings((current) => normalizeDisplaySettings({ ...current, ...patch }));
  }, [setCanvasZoom]);

  const handleResetDisplaySettings = useCallback(() => {
    canvasViewportRef.current = { x: 0, y: 0, zoom: DEFAULT_DISPLAY_SETTINGS.scale };
    setDisplaySettings({ ...DEFAULT_DISPLAY_SETTINGS });
    setCanvasOffset({ x: 0, y: 0 });
    revealZoomIndicator();
    revealMinimap();
  }, [revealMinimap, revealZoomIndicator]);

  const handleResetCanvasView = useCallback(() => {
    canvasViewportRef.current = { x: 0, y: 0, zoom: 1 };
    setDisplaySettings((current) => ({ ...current, scale: 1 }));
    setCanvasOffset({ x: 0, y: 0 });
    revealZoomIndicator();
    revealMinimap();
  }, [revealMinimap, revealZoomIndicator]);

  const handleCanvasMouseMove = useCallback((event) => {
    const selection = canvasSelectionRef.current;
    if (selection) {
      const currentPoint = {
        x: Math.min(
          selection.containerWidth,
          Math.max(0, event.clientX - selection.containerLeft)
        ),
        y: Math.min(
          selection.containerHeight,
          Math.max(0, event.clientY - selection.containerTop)
        ),
      };
      const nextBox = normalizeSelectionRect(selection.startPoint, currentPoint);
      selection.currentPoint = currentPoint;
      setSelectionBox(nextBox);
      return;
    }

    const pan = canvasPanRef.current;
    if (!pan) return;
    revealMinimap();
    const nextOffset = {
      x: pan.offsetX + event.clientX - pan.clientX,
      y: pan.offsetY + event.clientY - pan.clientY,
    };
    canvasViewportRef.current = { ...canvasViewportRef.current, ...nextOffset };
    setCanvasOffset(nextOffset);
  }, [revealMinimap]);

  const handleCanvasMouseUp = useCallback((event) => {
    const selection = canvasSelectionRef.current;
    const pan = canvasPanRef.current;
    const cancelled = event?.type === 'blur';
    if (selection && !cancelled) {
      const currentPoint = event ? {
        x: Math.min(
          selection.containerWidth,
          Math.max(0, event.clientX - selection.containerLeft)
        ),
        y: Math.min(
          selection.containerHeight,
          Math.max(0, event.clientY - selection.containerTop)
        ),
      } : selection.currentPoint;
      const finalBox = normalizeSelectionRect(selection.startPoint, currentPoint);
      const isMarquee = Math.max(finalBox.width, finalBox.height)
        >= WIDGET_SELECTION_DRAG_THRESHOLD;

      if (isMarquee) {
        const widgets = Array.from(
          dashboardCanvasRef.current?.querySelectorAll('.dockable-widget') || []
        ).map((widget) => {
            const rect = widget.getBoundingClientRect();
            return {
              id: widget.dataset.widgetId,
              rect: {
                left: rect.left - selection.containerLeft,
                top: rect.top - selection.containerTop,
                right: rect.right - selection.containerLeft,
                bottom: rect.bottom - selection.containerTop,
              },
            };
          });
        const matchedWidgetIds = getWidgetIdsInSelectionRect(widgets, finalBox);
        updateSelectedWidgetIds(mergeWidgetSelection(
          selection.initialSelection,
          matchedWidgetIds,
          selection.additive
        ));
      } else if (!selection.additive) {
        updateSelectedWidgetIds([]);
      }
    } else if (pan?.deselectOnClick && event && !cancelled) {
      const pointerTravel = Math.max(
        Math.abs(event.clientX - pan.clientX),
        Math.abs(event.clientY - pan.clientY)
      );
      if (pointerTravel < WIDGET_SELECTION_DRAG_THRESHOLD) {
        updateSelectedWidgetIds([]);
      }
    }

    canvasPanRef.current = null;
    canvasSelectionRef.current = null;
    setIsCanvasPanning(false);
    setIsCanvasSelecting(false);
    setSelectionBox(null);
    document.removeEventListener('mousemove', handleCanvasMouseMove);
    document.removeEventListener('mouseup', handleCanvasMouseUp);
    window.removeEventListener('blur', handleCanvasMouseUp);
  }, [handleCanvasMouseMove, updateSelectedWidgetIds]);

  const handleCanvasMouseDown = useCallback((event) => {
    if (!event.currentTarget.contains(event.target)) return;
    const isMiddleButton = event.button === 1;
    const isCanvasBackground = !event.target.closest('.dockable-widget')
      && !event.target.closest('.dashboard-overlay');
    const isSelectionModifier = event.ctrlKey || event.metaKey;
    const isAlternatePan = event.button === 0 && event.altKey && isCanvasBackground;
    const isSelectionStart = event.button === 0
      && isSelectionModifier
      && !event.altKey
      && isCanvasBackground;
    const isPrimaryPan = event.button === 0
      && !isSelectionModifier
      && !event.altKey
      && isCanvasBackground;
    if (!isMiddleButton && !isAlternatePan && !isPrimaryPan && !isSelectionStart) return;
    event.preventDefault();
    if (isSelectionStart && !isCompactCanvas) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const startPoint = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      canvasSelectionRef.current = {
        containerLeft: rect.left,
        containerTop: rect.top,
        containerWidth: rect.width,
        containerHeight: rect.height,
        startPoint,
        currentPoint: startPoint,
        additive: isSelectionModifier,
        initialSelection: [...selectedWidgetIdsRef.current],
      };
      setSelectionBox(normalizeSelectionRect(startPoint, startPoint));
      setIsCanvasSelecting(true);
    } else {
      revealMinimap();
      const liveViewport = canvasViewportRef.current;
      canvasPanRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
        offsetX: liveViewport.x,
        offsetY: liveViewport.y,
        deselectOnClick: isPrimaryPan,
      };
      setIsCanvasPanning(true);
    }

    document.addEventListener('mousemove', handleCanvasMouseMove);
    document.addEventListener('mouseup', handleCanvasMouseUp);
    window.addEventListener('blur', handleCanvasMouseUp);
  }, [
    handleCanvasMouseMove,
    handleCanvasMouseUp,
    isCompactCanvas,
    revealMinimap,
  ]);

  useEffect(() => () => {
    canvasPanRef.current = null;
    canvasSelectionRef.current = null;
    document.removeEventListener('mousemove', handleCanvasMouseMove);
    document.removeEventListener('mouseup', handleCanvasMouseUp);
    window.removeEventListener('blur', handleCanvasMouseUp);
  }, [handleCanvasMouseMove, handleCanvasMouseUp]);

  const handleCanvasKeyDown = useCallback((event) => {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.isComposing || isCompactCanvas || isSideMenuOpen) return;
    const target = event.target;
    if (target?.closest?.('input, textarea, select, button, a, [contenteditable]:not([contenteditable="false"]), [role="slider"], [role="spinbutton"], [role="listbox"], [role="combobox"], [role="menu"], [role="tablist"], [role="grid"], [role="tree"], [role="dialog"], .dashboard-overlay')) return;
    // Body focus is common after clicking a plot; no empty canvas area is required.
    if (target !== document.body && target !== document.documentElement && !containerRef.current?.contains(target)) return;
    const direction = { ArrowLeft: [1, 0], ArrowRight: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] }[event.key];
    if (!direction) return;
    event.preventDefault();
    const step = event.shiftKey ? 160 : 40;
    const current = canvasViewportRef.current;
    const next = { ...current, x: current.x + direction[0] * step, y: current.y + direction[1] * step };
    canvasViewportRef.current = next;
    setCanvasOffset({ x: next.x, y: next.y });
    revealMinimap();
  }, [isCompactCanvas, isSideMenuOpen, revealMinimap]);

  useEffect(() => {
    document.addEventListener('keydown', handleCanvasKeyDown);
    return () => document.removeEventListener('keydown', handleCanvasKeyDown);
  }, [handleCanvasKeyDown]);

  const handleCanvasWheel = useCallback((event) => {
    const action = getCanvasWheelAction(event);
    if (action === CANVAS_WHEEL_ACTIONS.IGNORE) return;

    event.preventDefault();
    if (action === CANVAS_WHEEL_ACTIONS.PREVENT_BROWSER_ZOOM) return;
    revealMinimap();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    if (action === CANVAS_WHEEL_ACTIONS.ZOOM) {
      if (!event.deltaY) return;
      setCanvasZoom(canvasViewportRef.current.zoom - Math.sign(event.deltaY) * ZOOM_STEP, {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      return;
    }

    const current = canvasViewportRef.current;
    const nextView = {
      ...current,
      x: current.x - event.deltaX,
      y: current.y - event.deltaY,
    };
    canvasViewportRef.current = nextView;
    setCanvasOffset({ x: nextView.x, y: nextView.y });
  }, [revealMinimap, setCanvasZoom]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.addEventListener('wheel', handleCanvasWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleCanvasWheel);
  }, [handleCanvasWheel]);

  const handleWidgetBindingChange = useCallback((widgetId, inputId, variableId) => {
    setWidgetInputBindings((current) => mergeWidgetInputBindings({
      ...current,
      [widgetId]: {
        ...(current[widgetId] || {}),
        [inputId]: variableId,
      },
    }));
  }, []);

  useEffect(() => {
    if (!demoMode) return;

    setCurationSource(null);
    setCurationSession(null);
    setCurationSpikeSelection([]);
    setCurationActionError('');
    setCurationRecoveryMessage('');
    curationBaseStateRef.current = null;

    const grouped = {};
    (demoClusterPlotData || []).forEach((point) => {
      const cid = point.clusterId;
      if (!grouped[cid]) grouped[cid] = [];
      grouped[cid].push(point);
    });

    const clusterIds = Object.keys(grouped).map(Number).sort((a, b) => a - b);

    const normalizedClusters = clusterIds.map((clusterId, clusterIdx) => {
      const pointsArray = grouped[clusterId] || [];
      return {
        clusterId,
        clusterLabel: `Cluster ${clusterId}`,
        points: pointsArray.map((p) => [p.x, p.y]),
        spikeTimes: pointsArray.map((_, pointIdx) => 100 + pointIdx * 20),
        spikeChannels: pointsArray.map(() => [179, 181, 183][clusterIdx % 3]),
        pointCount: pointsArray.length
      };
    });

    setClusters(
      normalizedClusters.map((cluster) => ({
        id: cluster.clusterId,
        size: cluster.pointCount
      }))
    );

    setClusterData({
      clusters: normalizedClusters,
      clusterIds,
      numClusters: normalizedClusters.length,
      totalPoints: normalizedClusters.reduce((sum, c) => sum + c.pointCount, 0)
    });

    setSelectedClusters(clusterIds.slice(0, 3));

    setSpikes((demoSpikeTable || []).map((row) => ({
      time: row.spikeTime,
      clusterId: row.assignedClusterId
    })));

    const normalizedStats = {};
    (demoClusterStats || []).forEach((row) => {
      normalizedStats[row.clusterId] = {
        clusterId: row.clusterId,
        count: row.count,
        numSpikes: row.count,
        peakChannel: [179, 181, 183][(row.clusterId - 1) % 3],
        firingRateHz: row.count / 8,
        isiViolationRate: ((row.clusterId * 7) % 13) / 1000,
        meanAmplitude: row.meanAmplitude
      };
    });
    setClusterStats(normalizedStats);
    setClusterWaveforms(demoWaveforms || {});
    setVisibleClusterFilterActive(false);
    setVisibleClusterOrder([]);
  }, [demoMode, demoClusterPlotData, demoSpikeTable, demoClusterStats, demoWaveforms]);

  useEffect(() => {
    if (demoMode) return undefined;

    const sameSource = lastClusterSourceRef.current.algorithm === selectedAlgorithm
      && lastClusterSourceRef.current.results === clusteringResults;
    lastClusterSourceRef.current = { algorithm: selectedAlgorithm, results: clusteringResults };
    // Loading or correcting the matching recording must not discard imported
    // curator clusters. Waveforms refresh separately using the recording scope.
    if (curatorDataset && sameSource) return undefined;
    let cancelled = false;
    handleWidgetLoadingChange('clusterList', true, 'Loading clusters…');
    handleWidgetLoadingChange('dimReduction', true, 'Updating cluster projection…');

    const clearClusterState = (preserveSelection = false) => {
      setCuratorDataset(null);
      setClusters([]);
      if (!preserveSelection) setSelectedClusters([]);
      setSpikes([]);
      setSelectedSpike(null);
      setClusterStats({});
      setClusterData(null);
      setClusterWaveforms({});
      setHighlightedSpikes([]);
      setCurationSpikeSelection([]);
      setCurationSource(null);
      setCurationSession(null);
      setCurationActionError('');
      setCurationRecoveryMessage('');
      curationBaseStateRef.current = null;
      setSelectedChannels([]);
      setFocusedTimeRange(null);
      setVisibleClusterFilterActive(false);
    };

    const applyClusterData = (data) => {
      const normalizedClusters = Array.isArray(data?.clusters) ? data.clusters : [];
      const clusterIds = normalizedClusters.map((cluster, index) =>
        cluster.clusterId ?? cluster.id ?? index
      );

      setClusterData({
        ...data,
        clusters: normalizedClusters,
        clusterIds
      });
      setClusters(normalizedClusters.map((cluster, index) => ({
        id: cluster.clusterId ?? cluster.id ?? index,
        size: cluster.pointCount ?? cluster.numSpikes ?? cluster.points?.length ?? 0
      })));
      setSelectedClusters((previous) => {
        const availableIds = new Set(clusterIds.map(String));
        const stillAvailable = previous.filter((id) => availableIds.has(String(id)));
        return stillAvailable.length > 0 ? stillAvailable : clusterIds.slice(0, 3);
      });
    };

    const loadClusterList = async () => {
      clearClusterState(true);

      try {
        if (selectedAlgorithm === 'preprocessed_kilosort4') {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const response = await authenticatedFetch(`${apiUrl}/api/cluster-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mode: 'real',
              channelMapping: {},
              algorithm: selectedAlgorithm
            })
          });

          if (!response.ok) {
            throw new Error(`Failed to load cluster data (${response.status})`);
          }

          const data = await response.json();
          if (!cancelled) applyClusterData(data);
          return;
        }

        if (clusteringResults?.available) {
          const resultClusters = Array.isArray(clusteringResults.fullData)
            ? clusteringResults.fullData.map((clusterSpikes, index) => {
                const summary = clusteringResults.clusters?.[index] || {};
                const clusterId = summary.clusterId ?? index;
                return {
                  clusterId,
                  points: (clusterSpikes || []).map((spike) => [spike.x, spike.y]),
                  spikeTimes: (clusterSpikes || []).map((spike) => spike.time),
                  spikeChannels: (clusterSpikes || []).map((spike) => spike.channel),
                  pointCount: summary.numSpikes ?? clusterSpikes?.length ?? 0
                };
              })
            : (clusteringResults.clusters || []);

          if (!cancelled) {
            applyClusterData({
              ...clusteringResults,
              clusters: resultClusters
            });
          }
          return;
        }

        if (!cancelled) setSelectedClusters([]);
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching cluster list:', error);
          clearClusterState(false);
        }
      } finally {
        if (!cancelled) {
          handleWidgetLoadingChange('clusterList', false);
          handleWidgetLoadingChange('dimReduction', false);
        }
      }
    };

    loadClusterList();
    return () => {
      cancelled = true;
    };
  }, [
    clusteringResults,
    demoMode,
    handleWidgetLoadingChange,
    selectedAlgorithm,
    selectedDataset,
  ]);

  useEffect(() => {
    if (demoMode) return undefined;

    if (selectedClusters.length === 0) {
      setSpikes([]);
      setClusterWaveforms({});
      handleWidgetLoadingChange('waveform', false);
      handleWidgetLoadingChange('amplitudeProfile', false);
      if (curatorDataset) handleWidgetLoadingChange('dimReduction', false);
      return undefined;
    }

    let cancelled = false;
    handleWidgetLoadingChange('waveform', true, 'Loading waveforms…');
    handleWidgetLoadingChange('amplitudeProfile', true, 'Updating amplitudes…');
    if (curatorDataset) {
      handleWidgetLoadingChange('dimReduction', true, 'Updating cluster projection…');
    }

    const loadSelectedClusterDetails = async () => {
      const clusterLookup = new Map(
        (clusterData?.clusters || []).map((cluster, index) => [
          String(cluster.clusterId ?? cluster.id ?? index),
          cluster
        ])
      );
      const nextSpikes = [];

      selectedClusters.forEach((clusterId) => {
        const cluster = clusterLookup.get(String(clusterId));
        (cluster?.spikeTimes || []).forEach((time, pointIndex) => {
          nextSpikes.push({
            time,
            clusterId,
            pointIndex,
            spikeIndex: cluster.spikeIndices?.[pointIndex] ?? pointIndex,
            spikeId: cluster.spikeIds?.[pointIndex]
              ?? `${clusterId}:${cluster.spikeIndices?.[pointIndex] ?? pointIndex}`,
            channel: cluster.spikeChannels?.[pointIndex] ?? cluster.channelId
          });
        });
      });

      nextSpikes.sort((a, b) => Number(a.time) - Number(b.time));
      setSpikes(nextSpikes);
      const embeddedWaveforms = createDashboardDataFromCuratorDataset(curatorDataset).clusterWaveforms;
      setClusterWaveforms(embeddedWaveforms);

      try {
        const selectedIds = new Set(selectedClusters.map(String));
        const explicitClusters = curatorDataset
          ? curatorDataset.clusters.filter((cluster) => selectedIds.has(String(cluster.id)))
          : [];
        const requestParams = {
          clusterIds: curatorDataset ? [] : selectedClusters,
          clusters: explicitClusters,
          maxWaveforms: curatorDataset ? 30 : 100,
          algorithm: selectedAlgorithm,
          includeSpikeIndices: highlightedSpikes,
        };
        const cacheKey = createSessionCacheKey('widget-data', [
          dataCacheScope,
          'waveforms',
          selectedClusters,
          requestParams.maxWaveforms,
          selectedAlgorithm,
          highlightedSpikes,
        ]);
        const waveformsData = await getOrLoadSessionCache(
          cacheKey,
          () => apiClient.getClusterWaveforms(requestParams)
        );

        if (!cancelled) {
          const extractedWaveforms = waveformsData.waveforms || {};
          const nonEmptyExtracted = Object.fromEntries(
            Object.entries(extractedWaveforms).filter(([, waveforms]) => (
              Array.isArray(waveforms) && waveforms.length > 0
            ))
          );
          setClusterWaveforms({
            ...nonEmptyExtracted,
            ...embeddedWaveforms,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching selected cluster details:', error);
        }
      } finally {
        if (!cancelled) {
          handleWidgetLoadingChange('waveform', false);
          handleWidgetLoadingChange('amplitudeProfile', false);
          if (curatorDataset) handleWidgetLoadingChange('dimReduction', false);
        }
      }
    };

    loadSelectedClusterDetails();
    return () => {
      cancelled = true;
    };
  }, [
    clusterData,
    curatorDataset,
    dataCacheScope,
    demoMode,
    handleWidgetLoadingChange,
    highlightedSpikes,
    selectedAlgorithm,
    selectedClusters,
  ]);

  useEffect(() => {
    if (demoMode) return undefined;
    if (curatorDataset) {
      // A source-menu change can finish its empty-state effect after the child
      // reports an import. Reconcile statistics from the committed source.
      const imported = createDashboardDataFromCuratorDataset(curatorDataset, datasetInfo).clusterStats;
      setClusterStats((previous) => Object.fromEntries(Object.entries(imported).map(([id, stats]) => [id, {
        ...stats, meanAmplitude: stats.meanAmplitude ?? previous[id]?.meanAmplitude ?? null,
      }])));
      handleWidgetLoadingChange('clusterStats', false);
      return undefined;
    }
    const clusterIds = clusterData?.clusterIds || [];
    if (clusterIds.length === 0) {
      setClusterStats({});
      handleWidgetLoadingChange('clusterStats', false);
      return undefined;
    }

    let cancelled = false;
    handleWidgetLoadingChange('clusterStats', true, 'Loading statistics…');
    apiClient.getClusterStatistics(clusterIds, selectedAlgorithm)
      .then((statisticsData) => {
        if (!cancelled) setClusterStats(statisticsData.statistics || {});
      })
      .catch((error) => {
        if (!cancelled) console.error('Error fetching cluster statistics:', error);
      })
      .finally(() => {
        if (!cancelled) handleWidgetLoadingChange('clusterStats', false);
      });

    return () => { cancelled = true; };
  }, [
    clusterData,
    curatorDataset,
    datasetInfo,
    demoMode,
    handleWidgetLoadingChange,
    selectedAlgorithm,
  ]);

  const handleClusterSelect = useCallback((clusterId, options = {}) => {
    const additive = Boolean(options.additive);
    setSelectedClusters((previous) => {
      if (!additive) return [clusterId];
      const alreadySelected = previous.some((id) => String(id) === String(clusterId));
      return alreadySelected
        ? previous.filter((id) => String(id) !== String(clusterId))
        : [...previous, clusterId];
    });
  }, []);

  const handleCuratorClusterSelect = useCallback((cluster, options = {}) => {
    handleClusterSelect(cluster.id, {
      ...options,
      additive: options.additive !== false,
    });
    setWidgetStates(revealCuratorLinkedWidgets);
  }, [handleClusterSelect]);

  const handleCuratorDatasetChange = useCallback((dataset) => {
    if (!dataset || !Array.isArray(dataset.clusters)) return;
    if (
      dataset.metadata?.sourceSignature
      && dataset.metadata.sourceSignature === curationSourceRef.current?.signature
    ) return;

    const normalizedDataset = normalizeCuratorDatasetTimes(dataset, datasetInfo);
    const dashboardData = createDashboardDataFromCuratorDataset(normalizedDataset, datasetInfo);
    const clusterIds = dashboardData.clusterData.clusterIds;

    setCuratorDataset({
      ...normalizedDataset,
      clusterLookup: dashboardData.clusterLookup,
    });
    setClusters(dashboardData.clusters);
    setClusterData(dashboardData.clusterData);
    setClusterStats(dashboardData.clusterStats);
    setSpikes([]);
    setSelectedSpike(null);
    setClusterWaveforms(dashboardData.clusterWaveforms);
    setHighlightedSpikes([]);
    setCurationSpikeSelection([]);
    setCurationSource(null);
    setCurationSession(null);
    setCurationActionError('');
    setCurationRecoveryMessage('');
    curationBaseStateRef.current = null;
    setSelectedChannels((previous) => {
      const channelCount = Number(datasetInfo?.totalChannels || 0);
      const valid = previous.filter((channel) => channel >= 1 && channel <= channelCount);
      return valid.length ? valid : getDefaultChannelSelection(datasetInfo);
    });
    setFocusedTimeRange(null);
    setVisibleClusterOrder(clusterIds);
    setVisibleClusterFilterActive(true);
    setSelectedClusters((previous) => (
      reconcileCuratorClusterSelection(previous, clusterIds)
    ));
  }, [datasetInfo]);

  const handleCuratorSelectionChange = useCallback((clusterIds) => {
    setSelectedClusters(Array.isArray(clusterIds) ? clusterIds : []);
  }, []);

  const pcaClusterData = useMemo(() => (
    curatorDataset
      ? createWaveformPcaClusterData(clusterData, clusterWaveforms)
      : clusterData
  ), [clusterData, clusterWaveforms, curatorDataset]);

  const handleClusterPairSelect = useCallback((primaryClusterId, secondaryClusterId) => {
    setSelectedClusters(String(primaryClusterId) === String(secondaryClusterId)
      ? [primaryClusterId]
      : [primaryClusterId, secondaryClusterId]);
  }, []);

  const handleSelectedChannelsChange = useCallback((channelIds) => {
    const totalChannels = Math.max(0, Number(datasetInfo?.totalChannels || 0));
    const normalized = (channelIds || []).map(Number).filter((channelId, index, values) => (
      Number.isInteger(channelId)
      && channelId >= 1
      && channelId <= totalChannels
      && values.indexOf(channelId) === index
    ));
    setSelectedChannels(normalized);
  }, [datasetInfo]);

  const handleChannelSelect = useCallback((channelId, options = {}) => {
    const numericChannel = Number(channelId);
    const totalChannels = Math.max(0, Number(datasetInfo?.totalChannels || 0));
    if (!Number.isInteger(numericChannel) || numericChannel < 1 || numericChannel > totalChannels) return;
    setSelectedChannels((current) => {
      if (!options.additive) return [numericChannel];
      return current.includes(numericChannel)
        ? current.filter((candidate) => candidate !== numericChannel)
        : [...current, numericChannel];
    });
  }, [datasetInfo]);

  const handleSpikeHighlight = useCallback((clusterOrEvent, pointIndex) => {
    const suppliedEvent = clusterOrEvent && typeof clusterOrEvent === 'object' ? clusterOrEvent : null;
    const clusterId = suppliedEvent?.clusterId ?? clusterOrEvent;
    const resolvedPointIndex = suppliedEvent?.pointIndex ?? suppliedEvent?.spikeIndex ?? pointIndex;
    const cluster = (clusterData?.clusters || []).find((candidate, index) => (
      String(candidate.clusterId ?? candidate.id ?? index) === String(clusterId)
    ));
    const event = {
      spikeId: suppliedEvent?.spikeId ?? cluster?.spikeIds?.[resolvedPointIndex],
      clusterId,
      spikeIndex: suppliedEvent?.spikeIndex ?? cluster?.spikeIndices?.[resolvedPointIndex] ?? resolvedPointIndex,
      pointIndex: Number.isFinite(Number(resolvedPointIndex)) ? Number(resolvedPointIndex) : 0,
      time: suppliedEvent?.time ?? suppliedEvent?.timeSamples ?? cluster?.spikeTimes?.[resolvedPointIndex],
      channel: suppliedEvent?.channel ?? cluster?.spikeChannels?.[resolvedPointIndex] ?? cluster?.channelId,
    };

    setHighlightedSpikes([event]);
    if (!suppliedEvent?.clusterSelectionHandled) {
      setSelectedClusters((previous) => previous.some((id) => String(id) === String(clusterId))
        ? previous
        : [clusterId, ...previous]);
    }
  }, [clusterData]);

  const handleCurationSpikeSelection = useCallback((selection) => {
    const normalized = [...new Map((Array.isArray(selection) ? selection : [])
      .filter((spike) => spike && spike.clusterId !== undefined)
      .map((spike) => {
        const spikeId = spike.spikeId
          || `${spike.clusterId}:${spike.spikeIndex ?? spike.pointIndex ?? 0}`;
        return [spikeId, { ...spike, spikeId }];
      })).values()];
    setCurationSpikeSelection(normalized);
    if (normalized.length > 0) handleSpikeHighlight(normalized[0]);
    else setHighlightedSpikes([]);
  }, [handleSpikeHighlight]);

  const applyCurationState = useCallback((source, nextSession, preferredClusterIds = null) => {
    const dashboard = createCuratedDashboardData(
      source,
      nextSession,
      clusterAnnotations,
      datasetInfo
    );
    const availableIds = dashboard.clusterData.clusterIds;
    setCurationSession(nextSession);
    setClusters(dashboard.clusters);
    setClusterData(dashboard.clusterData);
    setClusterStats(dashboard.clusterStats);
    setCuratorDataset(dashboard.curatorDataset);
    setClusterWaveforms(dashboard.clusterWaveforms);
    setSpikes([]);
    setSelectedSpike(null);
    setHighlightedSpikes([]);
    setCurationSpikeSelection([]);
    setFocusedTimeRange(null);
    setVisibleClusterOrder(availableIds);
    setSelectedClusters((previous) => {
      const requested = Array.isArray(preferredClusterIds) ? preferredClusterIds : previous;
      const available = new Set(availableIds.map(String));
      const retained = requested.filter((clusterId) => available.has(String(clusterId)));
      return retained.length ? retained : availableIds.slice(0, 1);
    });
  }, [clusterAnnotations, datasetInfo]);

  useEffect(() => {
    if (curationSource || !Array.isArray(clusterData?.clusters) || clusterData.clusters.length === 0) {
      return;
    }
    const source = createCurationSource({
      sourceKey: curationStorageKey,
      clusterData,
      clusteringResults: curatorDataset ? null : clusteringResults,
      clusterWaveforms,
      clusterAnnotations,
    });
    curationBaseStateRef.current = {
      clusters,
      clusterData,
      clusterStats,
      clusterWaveforms,
      curatorDataset,
      selectedClusters,
      visibleClusterOrder,
    };
    const saved = localStorage.getItem(curationStorageKey);
    const restored = saved
      ? restoreCurationSession(saved, source)
      : { session: createCurationSession(source), recovered: true, error: null };
    setCurationSource(source);
    setCurationSession(restored.session);
    setCurationRecoveryMessage(saved && !restored.recovered
      ? `${restored.error} A clean session was recovered; original sorter data was untouched.`
      : '');
    if (restored.session.operations.length > 0) {
      const restoredState = deriveCurationState(source, restored.session);
      setClusterAnnotations((previous) => {
        const next = { ...previous };
        restoredState.clusters.forEach((cluster) => {
          if (!next[String(cluster.id)]) next[String(cluster.id)] = cluster.metadata;
        });
        localStorage.setItem(annotationStorageKey, JSON.stringify(next));
        return next;
      });
      applyCurationState(source, restored.session);
    }
  }, [
    applyCurationState,
    clusterAnnotations,
    clusterData,
    clusterStats,
    clusterWaveforms,
    clusteringResults,
    clusters,
    annotationStorageKey,
    curationSource,
    curationStorageKey,
    curatorDataset,
    selectedClusters,
    visibleClusterOrder,
  ]);

  useEffect(() => {
    if (!curationSource || !curationSession) return;
    localStorage.setItem(curationStorageKey, JSON.stringify(curationSession));
  }, [curationSession, curationSource, curationStorageKey]);

  const persistOperationMetadata = useCallback((session) => {
    const operation = session.operations[session.cursor - 1];
    if (!operation?.metadata) return;
    setClusterAnnotations((previous) => {
      const next = {
        ...previous,
        [String(operation.targetClusterId)]: operation.metadata,
      };
      localStorage.setItem(annotationStorageKey, JSON.stringify(next));
      return next;
    });
  }, [annotationStorageKey]);

  const handleMergeCuration = useCallback(() => {
    if (!curationSource || !curationSession) return;
    try {
      const next = mergeCurationClusters(
        curationSource,
        curationSession,
        selectedClusters,
        clusterAnnotations
      );
      const operation = next.operations[next.cursor - 1];
      persistOperationMetadata(next);
      applyCurationState(curationSource, next, [operation.targetClusterId]);
      setCurationActionError('');
    } catch (error) {
      setCurationActionError(error.message || 'Unable to merge the selected clusters.');
    }
  }, [
    applyCurationState,
    clusterAnnotations,
    curationSession,
    curationSource,
    persistOperationMetadata,
    selectedClusters,
  ]);

  const handleSplitCuration = useCallback(() => {
    if (!curationSource || !curationSession) return;
    try {
      const next = splitCurationSelection(
        curationSource,
        curationSession,
        curationSpikeSelection.map((spike) => spike.spikeId),
        clusterAnnotations
      );
      const operation = next.operations[next.cursor - 1];
      persistOperationMetadata(next);
      applyCurationState(
        curationSource,
        next,
        [operation.sourceClusterId, operation.targetClusterId]
      );
      setCurationActionError('');
    } catch (error) {
      setCurationActionError(error.message || 'Unable to split the selected spikes.');
    }
  }, [
    applyCurationState,
    clusterAnnotations,
    curationSession,
    curationSource,
    curationSpikeSelection,
    persistOperationMetadata,
  ]);

  const handleUndoCuration = useCallback(() => {
    if (!curationSource || !curationSession) return;
    const next = undoCuration(curationSession);
    applyCurationState(curationSource, next);
    setCurationActionError('');
  }, [applyCurationState, curationSession, curationSource]);

  const handleRedoCuration = useCallback(() => {
    if (!curationSource || !curationSession) return;
    const next = redoCuration(curationSession);
    persistOperationMetadata(next);
    applyCurationState(curationSource, next);
    setCurationActionError('');
  }, [applyCurationState, curationSession, curationSource, persistOperationMetadata]);

  const handleResetCuration = useCallback(() => {
    if (!curationSource) return;
    const fresh = createCurationSession(curationSource);
    const base = curationBaseStateRef.current;
    const targetIds = new Set((curationSession?.operations || []).map((operation) => (
      String(operation.targetClusterId)
    )));
    setClusterAnnotations((previous) => {
      const next = Object.fromEntries(Object.entries(previous).filter(([clusterId]) => (
        !targetIds.has(String(clusterId))
      )));
      localStorage.setItem(annotationStorageKey, JSON.stringify(next));
      return next;
    });
    setCurationSession(fresh);
    setCurationSpikeSelection([]);
    setHighlightedSpikes([]);
    setCurationActionError('');
    setCurationRecoveryMessage('');
    if (!base) {
      applyCurationState(curationSource, fresh);
      return;
    }
    setClusters(base.clusters);
    setClusterData(base.clusterData);
    setClusterStats(base.clusterStats);
    setClusterWaveforms(base.clusterWaveforms);
    setCuratorDataset(base.curatorDataset);
    setSelectedClusters(base.selectedClusters);
    setVisibleClusterOrder(base.visibleClusterOrder);
    setSpikes([]);
    setSelectedSpike(null);
    setFocusedTimeRange(null);
  }, [annotationStorageKey, applyCurationState, curationSession, curationSource]);

  const curationDerivedState = useMemo(() => {
    if (!curationSource || !curationSession) return null;
    try {
      return deriveCurationState(curationSource, curationSession);
    } catch (_error) {
      return null;
    }
  }, [curationSession, curationSource]);
  const curationActive = Boolean(curationSession?.operations?.length);
  const useLocalClusterData = demoMode || Boolean(curatorDataset) || curationActive;
  const curationExportPayload = useMemo(() => {
    if (!curationSource || !curationSession) return null;
    try {
      return createCurationExport({
        source: curationSource,
        session: curationSession,
        clusterAnnotations,
        dataset: selectedDataset && typeof selectedDataset === 'object'
          ? { id: selectedDataset.id ?? null, name: selectedDataset.name ?? selectedDataset.filename ?? null }
          : selectedDataset,
        algorithm: selectedAlgorithm,
      });
    } catch (_error) {
      return null;
    }
  }, [
    clusterAnnotations,
    curationSession,
    curationSource,
    selectedAlgorithm,
    selectedDataset,
  ]);
  const curationExportFilename = useMemo(() => {
    const rawName = selectedDataset?.name || selectedDataset?.filename || selectedDataset || 'dataset';
    const safeName = String(rawName).replace(/[^a-zA-Z0-9_.-]+/g, '-');
    return `spikescope-${safeName || 'dataset'}-curation-r${curationSession?.revision || 0}.json`;
  }, [curationSession?.revision, selectedDataset]);

  useEffect(() => {
    setHighlightedSpikes((previous) => previous.filter((spike) => (
      selectedClusters.some((clusterId) => String(clusterId) === String(spike.clusterId))
    )));
  }, [selectedClusters]);

  const handleAnnotationChange = useCallback((clusterId, patch) => {
    setClusterAnnotations((previous) => {
      const key = String(clusterId);
      const next = {
        ...previous,
        [key]: { ...(previous[key] || {}), ...patch },
      };
      localStorage.setItem(annotationStorageKey, JSON.stringify(next));
      return next;
    });
    if (patch.group) {
      setWidgetStates((previous) => ({
        ...previous,
        clusterList: {
          ...previous.clusterList,
          clusterGroups: {
            ...(previous.clusterList?.clusterGroups || {}),
            [String(clusterId)]: patch.group,
          },
        },
      }));
    }
  }, [annotationStorageKey]);

  const handleClusterGroupsChange = useCallback((nextGroups, change = null) => {
    const normalizedGroups = normalizeClusterGroups(nextGroups);
    setClusterGroupNames(normalizedGroups);
    localStorage.setItem(clusterGroupStorageKey, JSON.stringify(normalizedGroups));

    const sourceGroup = change?.renamedFrom || change?.deletedGroup;
    const targetGroup = change?.renamedTo || change?.replacementGroup;

    if (sourceGroup && targetGroup) {
      setClusterAnnotations((previous) => {
        const next = Object.fromEntries(Object.entries(previous).map(([clusterId, annotation]) => [
          clusterId,
          annotation?.group === sourceGroup
            ? { ...annotation, group: targetGroup }
            : annotation,
        ]));
        localStorage.setItem(annotationStorageKey, JSON.stringify(next));
        return next;
      });
    }
    setWidgetStates((previous) => {
      const clusterGroups = previous.clusterList?.clusterGroups || {};
      return {
        ...previous,
        clusterList: {
          ...previous.clusterList,
          groupNames: normalizedGroups,
          clusterGroups: Object.fromEntries(
            Object.entries(clusterGroups).map(([clusterId, group]) => [
              clusterId,
              sourceGroup && targetGroup && group === sourceGroup ? targetGroup : group,
            ])
          ),
        },
      };
    });
  }, [annotationStorageKey, clusterGroupStorageKey]);

  const handleVisibleClusterOrderChange = useCallback((clusterIds) => {
    setVisibleClusterFilterActive(true);
    setVisibleClusterOrder((previous) => {
      if (previous.length === clusterIds.length && previous.every((id, index) => String(id) === String(clusterIds[index]))) {
        return previous;
      }
      return clusterIds;
    });
  }, []);

  const populationClusterIds = useMemo(() => (
    visibleClusterFilterActive
      ? visibleClusterOrder
      : clusterData?.clusterIds || clusters.map((cluster) => cluster.id)
  ), [clusterData, clusters, visibleClusterFilterActive, visibleClusterOrder]);

  const handleAmplitudeSummaries = useCallback((summaries, fullProfile = false) => {
    setClusterStats((previous) => {
      let changed = false;
      const next = { ...previous };
      Object.entries(summaries || {}).forEach(([clusterId, summary]) => {
        if (summary?.meanAmplitude == null) return;
        const meanAmplitude = Number(summary.meanAmplitude);
        if (!Number.isFinite(meanAmplitude)) return;
        const current = previous[clusterId] || {};
        if (!fullProfile && current.meanAmplitude != null) return;
        if (current.meanAmplitude === meanAmplitude) return;
        next[clusterId] = { ...current, meanAmplitude };
        changed = true;
      });
      return changed ? next : previous;
    });
  }, []);

  const handleTimeRangeSelect = useCallback((range) => {
    const start = Number(range?.start);
    const end = Number(range?.end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;
    const orderedStart = Math.max(0, Math.min(start, end));
    const orderedEnd = Math.max(start, end);
    const next = {
      start: orderedStart,
      end: orderedEnd > orderedStart ? orderedEnd : orderedStart + 1,
    };
    setFocusedTimeRange((previous) => {
      if (previous?.start === next.start && previous?.end === next.end) return previous;
      return next;
    });
  }, []);

  const getWidgetPanelRecords = useCallback((widgetIds) => {
    const requestedIds = new Set((widgetIds || []).map(String));
    const records = {};

    dashboardCanvasRef.current?.querySelectorAll('.dockable-widget').forEach((widget) => {
      const widgetId = widget.dataset.widgetId;
      const panel = widget.parentElement;
      if (
        !widgetId ||
        !requestedIds.has(String(widgetId)) ||
        !panel
      ) {
        return;
      }

      records[widgetId] = {
        panel,
        position: getPanelPosition(panel),
        inlineLeft: panel.style.left,
        inlineTop: panel.style.top,
        inlineTransform: panel.style.transform,
        inlineWillChange: panel.style.willChange,
      };
    });

    return records;
  }, []);

  const cancelWidgetGroupDrag = useCallback(() => {
    const drag = widgetGroupDragRef.current;
    Object.values(drag?.records || {}).forEach((record) => {
      record.panel.style.left = record.inlineLeft;
      record.panel.style.top = record.inlineTop;
      record.panel.style.transform = record.inlineTransform;
      record.panel.style.willChange = record.inlineWillChange;
    });
    widgetGroupDragRef.current = null;
    setGroupDraggingWidgetIds([]);
  }, []);

  const handleWidgetDragStart = useCallback((widgetId) => {
    const isPartOfSelection = selectedWidgetIdsRef.current.some(
      (candidate) => String(candidate) === String(widgetId)
    );
    const requestedIds = isPartOfSelection
      ? selectedWidgetIdsRef.current
      : [widgetId];
    const records = getWidgetPanelRecords(requestedIds);
    if (!records[widgetId]) {
      widgetGroupDragRef.current = null;
      return;
    }

    widgetGroupDragRef.current = {
      leaderId: widgetId,
      records,
      positions: Object.fromEntries(Object.entries(records).map(([id, record]) => [
        id,
        record.position,
      ])),
      lastDelta: { x: 0, y: 0 },
    };
    setGroupDraggingWidgetIds(Object.keys(records));
  }, [getWidgetPanelRecords]);

  const handleWidgetDragMove = useCallback((widgetId, { delta, position }) => {
    const drag = widgetGroupDragRef.current;
    if (!drag || String(drag.leaderId) !== String(widgetId)) return;

    const leaderStart = drag.positions[widgetId];
    const effectiveDelta = position && leaderStart
      ? {
          x: position.left - leaderStart.left,
          y: position.top - leaderStart.top,
        }
      : delta;
    drag.lastDelta = effectiveDelta;
    const nextPositions = offsetWidgetPositions(drag.positions, effectiveDelta);
    Object.entries(nextPositions).forEach(([id, position]) => {
      if (String(id) === String(widgetId)) return;
      const panel = drag.records[id]?.panel;
      if (!panel) return;
      panel.style.willChange = 'transform';
      panel.style.transform = `${drag.records[id].inlineTransform} translate3d(${effectiveDelta.x}px, ${effectiveDelta.y}px, 0)`.trim();
    });
  }, []);

  const handleWidgetDragEnd = useCallback((
    widgetId,
    { moved = true, cancelled = false } = {}
  ) => {
    const drag = widgetGroupDragRef.current;
    if (cancelled) {
      cancelWidgetGroupDrag();
      return true;
    }

    if (!moved) {
      widgetGroupDragRef.current = null;
      setGroupDraggingWidgetIds([]);
      updateSelectedWidgetIds([widgetId]);
      return true;
    }

    if (!drag || String(drag.leaderId) !== String(widgetId)) return false;

    const finalPositions = offsetWidgetPositions(drag.positions, drag.lastDelta);
    Object.entries(finalPositions).forEach(([id, position]) => {
      const record = drag.records[id];
      if (!record) return;
      record.panel.style.left = `${position.left}px`;
      record.panel.style.top = `${position.top}px`;
      record.panel.style.transform = record.inlineTransform;
      record.panel.style.willChange = record.inlineWillChange;
    });
    setWidgetStates((previous) => {
      const next = { ...previous };
      Object.entries(finalPositions).forEach(([id, position]) => {
        if (!previous[id]) return;
        next[id] = {
          ...previous[id],
          position,
        };
      });
      return next;
    });

    widgetGroupDragRef.current = null;
    setGroupDraggingWidgetIds([]);
    revealMinimap();
    return true;
  }, [cancelWidgetGroupDrag, revealMinimap, updateSelectedWidgetIds]);

  const handleWidgetLayoutChange = useCallback((widgetId, layout) => {
    revealMinimap();
    setWidgetStates((prev) => ({
      ...prev,
      [widgetId]: {
        ...prev[widgetId],
        position: layout.position ? { ...layout.position } : prev[widgetId].position,
        size: layout.size ? { ...layout.size } : prev[widgetId].size
      }
    }));
  }, [revealMinimap]);

  const handleToggleWidget = useCallback((widgetId) => {
    cancelWidgetGroupDrag();
    setWidgetStates((prev) => {
      const next = {
        ...prev,
        [widgetId]: {
          ...prev[widgetId],
          visible: !prev[widgetId].visible,
          minimized: false
        }
      };
      return next;
    });
  }, [cancelWidgetGroupDrag]);

  const handleMinimizeWidget = useCallback((widgetId) => {
    cancelWidgetGroupDrag();
    setWidgetStates((prev) => ({
      ...prev,
      [widgetId]: {
        ...prev[widgetId],
        minimized: !prev[widgetId].minimized
      }
    }));
  }, [cancelWidgetGroupDrag]);

  const handleCloseWidget = useCallback((widgetId) => {
    cancelWidgetGroupDrag();
    setWidgetStates((prev) => ({
      ...prev,
      [widgetId]: {
        ...prev[widgetId],
        visible: false
      }
    }));
  }, [cancelWidgetGroupDrag]);

  const handleResetLayout = useCallback(() => {
    cancelWidgetGroupDrag();
    updateSelectedWidgetIds([]);
    if (isDefaultView) {
      setWidgetStates(DEFAULT_WIDGET_STATES);
      return;
    }

    setWidgetStates((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = {
          ...next[key],
          position: null,
          size: null
        };
      });
      return next;
    });
  }, [cancelWidgetGroupDrag, isDefaultView, updateSelectedWidgetIds]);

  const handleViewChange = useCallback((newWidgetStates, nextViewId, savedViewport) => {
    cancelWidgetGroupDrag();
    const appliedViewId = nextViewId
      || localStorage.getItem(getScopedStorageKey(CURRENT_VIEW_KEY, layoutStorageScope))
      || 'default';
    if (appliedViewId !== currentViewIdRef.current) {
      updateSelectedWidgetIds([]);
    }
    currentViewIdRef.current = appliedViewId;
    const nextWidgetStates = mergeWidgetStateDefaults(
      JSON.parse(JSON.stringify(newWidgetStates))
    );
    const restoredViewport = normalizeCanvasViewport(savedViewport);

    setWidgetStates(nextWidgetStates);
    setCurrentViewId(appliedViewId);

    if (restoredViewport) {
      const restoredScale = normalizeDisplaySettings({ scale: restoredViewport.zoom }).scale;
      canvasViewportRef.current = {
        x: restoredViewport.x,
        y: restoredViewport.y,
        zoom: restoredScale,
      };
      setCanvasOffset({ x: restoredViewport.x, y: restoredViewport.y });
      setDisplaySettings((current) => ({ ...current, scale: restoredScale }));
      return;
    }

    const container = containerRef.current;
    const rect = container?.getBoundingClientRect();
    const centeredViewport = getWidgetStatesCenteredViewport({
      containerWidth: rect?.width || container?.clientWidth || 0,
      containerHeight: rect?.height || container?.clientHeight || 0,
      widgetStates: nextWidgetStates,
      zoom: displayScaleRef.current,
    });
    canvasViewportRef.current = centeredViewport;
    setCanvasOffset({ x: centeredViewport.x, y: centeredViewport.y });
  }, [cancelWidgetGroupDrag, layoutStorageScope, updateSelectedWidgetIds]);

  const handleAddWidget = useCallback((widget) => {
    if (!canAccessWidget(widget.id, widgetUser)) return;
    const definition = WIDGET_DEFINITIONS[widget.id];
    const container = containerRef.current;

    setWidgetStates((prev) => {
      const existing = prev[widget.id] || {};
      const size = existing.size || definition?.defaultSize || { width: 300, height: 220 };
      const order = getHighestWidgetOrder(prev) + 1;
      const rect = container?.getBoundingClientRect();
      const focusedPosition = rect
        ? getViewportCenteredWidgetPosition({
            containerWidth: rect.width,
            containerHeight: rect.height,
            widgetWidth: size.width,
            widgetHeight: size.height,
            viewport: canvasViewportRef.current,
          })
        : { top: 80, left: 80 };
      return {
        ...prev,
        [widget.id]: {
          ...existing,
          visible: true,
          minimized: false,
          position: dropPosition || focusedPosition,
          size,
          order
        }
      };
    });

    setDropPosition(null);
  }, [dropPosition, widgetUser]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDefaultView) return;

    setIsDragOver(true);

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const canvasPoint = screenToCanvasPoint({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }, canvasViewportRef.current);
      setDropPosition({
        top: canvasPoint.top - 25,
        left: canvasPoint.left - 100,
      });
    }
  }, [isDefaultView]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (isDefaultView) return;
    setIsDragOver(false);
    setDropPosition(null);
  }, [isDefaultView]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDefaultView) return;

    setIsDragOver(false);

    try {
      const widgetData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (widgetData?.id) {
        handleAddWidget(widgetData);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }

    setDropPosition(null);
  }, [handleAddWidget, isDefaultView]);

  const getWidgetPositionsAndSizes = useCallback(() => widgetStates, [widgetStates]);

  useImperativeHandle(ref, () => ({
    handleToggleWidget,
    handleResetLayout,
    handleViewChange,
    getWidgetPositionsAndSizes,
    widgetStates,
    isWidgetBankOpen,
    setIsWidgetBankOpen
  }), [
    handleToggleWidget,
    handleResetLayout,
    handleViewChange,
    getWidgetPositionsAndSizes,
    widgetStates,
    isWidgetBankOpen
  ]);

  const highestWidgetOrder = useMemo(
    () => getHighestWidgetOrder(widgetStates),
    [widgetStates]
  );

  const getPanelStyle = useCallback((widgetId, isGroupedDrag = false) => {
    const state = widgetStates[widgetId];
    if (!state) return {};
    const stateOrder = Number(state.order);
    const zIndex = isGroupedDrag
      ? highestWidgetOrder + 1
      : Number.isFinite(stateOrder)
      ? stateOrder
      : 0;

    return {
      ...(state.position ? {
        top: `${state.position.top}px`,
        left: `${state.position.left}px`
      } : {}),
      zIndex
    };
  }, [highestWidgetOrder, widgetStates]);

  const getWidgetStyle = useCallback((widgetId) => {
    const state = widgetStates[widgetId];
    if (!state?.size) return {};

    return {
      width: `${state.size.width}px`,
      height: `${state.size.height}px`,
      flex: 'none'
    };
  }, [widgetStates]);

  const pipelineVariables = useMemo(() => createDashboardPipelineVariables({
    clusters,
    selectedClusters,
    visibleClusterOrder,
    spikes,
    highlightedSpikes,
    curationSpikeSelection,
    curationSession,
    selectedChannels,
    clusterStats,
    clusterAnnotations,
    clusterData,
    clusterWaveforms,
    clusteringResults,
    signalData: demoSignalData,
    datasetInfo,
    focusedTimeRange,
  }), [
    clusterAnnotations,
    clusterData,
    clusterStats,
    clusterWaveforms,
    clusteringResults,
    clusters,
    datasetInfo,
    demoSignalData,
    focusedTimeRange,
    curationSession,
    curationSpikeSelection,
    highlightedSpikes,
    selectedChannels,
    selectedClusters,
    spikes,
    visibleClusterOrder,
  ]);
  const analysisManifest = useMemo(() => createAnalysisManifest({
    apiBaseUrl: process.env.REACT_APP_API_URL
      || (typeof window !== 'undefined' ? window.location.origin : ''),
    demoMode,
    selectedDataset,
    selectedAlgorithm,
    datasetInfo,
    clusters,
    selectedClusters,
    visibleClusterOrder,
    clusterStats,
    clusterAnnotations,
    highlightedSpikes,
    focusedTimeRange,
    currentViewId,
    widgetStates,
    widgetInputBindings,
    pipelineVariables,
  }), [
    clusterAnnotations,
    clusterStats,
    clusters,
    currentViewId,
    datasetInfo,
    demoMode,
    focusedTimeRange,
    highlightedSpikes,
    pipelineVariables,
    selectedAlgorithm,
    selectedClusters,
    selectedDataset,
    visibleClusterOrder,
    widgetInputBindings,
    widgetStates,
  ]);
  const aiWidgetData = useMemo(() => generatedWidgetData({ clusters, selectedClusters, clusterStats,
    datasetInfo, selectedAlgorithm, focusedTimeRange }),
    [clusters, selectedClusters, clusterStats, datasetInfo, selectedAlgorithm, focusedTimeRange]);
  const renderDockable = (widgetId, title, body, panelClassName) => {
    const state = widgetStates[widgetId];
    if (!state?.visible || !canAccessWidget(widgetId, widgetUser)) return null;
    const isSelectedWidget = selectedWidgetIds.some(
      (candidate) => String(candidate) === String(widgetId)
    );
    const isGroupedDrag = groupDraggingWidgetIds.some(
      (candidate) => String(candidate) === String(widgetId)
    );

    return (
      <div
        className={`panel ${panelClassName} ${isSelectedWidget ? 'widget-selected' : ''} ${isGroupedDrag ? 'widget-group-dragging' : ''}`}
        data-widget-panel-id={widgetId}
        style={getPanelStyle(widgetId, isGroupedDrag)}
      >
        <DockableWidget
          id={widgetId}
          title={title}
          onClose={handleCloseWidget}
          onMinimize={handleMinimizeWidget}
          onLayoutChange={handleWidgetLayoutChange}
          isMinimized={state.minimized}
          draggable={!isCompactCanvas}
          resizable={!isCompactCanvas}
          freezeContentDuringResize={widgetId !== 'rasterPlot'}
          interactionScale={displaySettings.scale}
          layoutPosition={state.position}
          style={getWidgetStyle(widgetId)}
          isLoading={Boolean(widgetLoading[widgetId]?.loading)}
          loadingLabel={widgetLoading[widgetId]?.label}
          isSelected={isSelectedWidget}
          isGroupDragging={isGroupedDrag}
          onSelect={handleWidgetSelect}
          onDragStart={handleWidgetDragStart}
          onDragMove={handleWidgetDragMove}
          onDragEnd={handleWidgetDragEnd}
        >
          {body}
        </DockableWidget>
      </div>
    );
  };

  return (
    <div
      className={`multi-panel-view density-${displaySettings.density} ${isDragOver ? 'drag-over' : ''} ${isCanvasPanning ? 'canvas-panning' : ''} ${isCanvasSelecting ? 'canvas-selecting' : ''} ${groupDraggingWidgetIds.length ? 'group-dragging' : ''}`}
      ref={containerRef}
      tabIndex={0}
      aria-label="Widget canvas"
      title="Use arrow keys to pan; hold Shift to move faster"
      onMouseDownCapture={(event) => {
        if (!event.currentTarget.contains(event.target)) return;
        if (!event.target.closest('.dockable-widget, .dashboard-overlay, input, button, select, textarea, [contenteditable]')) {
          event.currentTarget.focus({ preventScroll: true });
        }
      }}
      style={{
        '--canvas-zoom': displaySettings.scale,
        '--canvas-offset-x': `${canvasOffset.x}px`,
        '--canvas-offset-y': `${canvasOffset.y}px`,
      }}
      onMouseDown={handleCanvasMouseDown}
      onMouseMove={handleCursorPositionChange}
      onMouseLeave={() => cursorCoordinatesRef.current?.hide()}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="canvas-grid" ref={canvasGridRef} aria-hidden="true"
        style={{ transform: `translate(${canvasOffset.x % (24 * displaySettings.scale)}px, ${canvasOffset.y % (24 * displaySettings.scale)}px)` }} />
      <div className="dashboard-overlay">
        {canUseDashboardAssistant(widgetUser) && !demoMode && <DashboardAssistant
          key={`${widgetUser.id}:${currentViewId}`}
          widgetData={aiWidgetData}
          onPublishWidget={widget => {
            setGeneratedWidgets(previous => [...previous.filter(w => w.id !== widget.id), widget]);
            if (widgetStates[widget.id]) {
              setWidgetStates(previous => ({ ...previous, [widget.id]: {
                ...previous[widget.id], visible: true, minimized: false,
              } }));
            } else {
              handleAddWidget({ id: widget.id });
            }
          }}
          onRemoveWidget={widgetId => {
            setGeneratedWidgets(previous => previous.filter(w => w.id !== widgetId));
            setWidgetStates(previous => { const next = { ...previous }; delete next[widgetId]; return next; });
          }}
          widgets={Object.values(WIDGET_DEFINITIONS)
            .filter(w => canAccessWidget(w.id, widgetUser) && (!w.id.startsWith('sossa') || isSossaDataset(curatorDataset)))
            .map(w => ({ id: w.id, name: w.name, description: w.description,
              visible: widgetStates[w.id]?.visible === true, minimized: widgetStates[w.id]?.minimized === true }))}
          onApply={(actions) => {
            if (!Array.isArray(actions) || actions.length > 12 || actions.some(a =>
              !WIDGET_DEFINITIONS[a.widgetId] || !canAccessWidget(a.widgetId, widgetUser) ||
              (a.widgetId.startsWith('sossa') && !isSossaDataset(curatorDataset)) ||
              !['show', 'hide', 'minimize', 'restore'].includes(a.operation))) {
              throw new Error('These widget actions are no longer available. Please ask again.');
            }
            cancelWidgetGroupDrag();
            actions.forEach(a => {
              if (a.operation === 'show' || a.operation === 'restore') handleAddWidget({ id: a.widgetId });
              else setWidgetStates(previous => ({ ...previous, [a.widgetId]: {
                ...previous[a.widgetId], ...(a.operation === 'hide' ? { visible: false } : { minimized: true }),
              } }));
            });
          }}
        />}

        <button
          className={`widget-bank-floating-toggle ${isWidgetBankOpen ? 'active' : ''}`}
          onClick={() => setIsWidgetBankOpen((prev) => !prev)}
          type="button"
          title="Open widgets"
        >
          <span className="widget-bank-floating-icon">+</span>
          <span className="widget-bank-floating-text">Widgets</span>
        </button>

        <WidgetBank
          sossaEnabled={isSossaDataset(curatorDataset)}
          isOpen={isWidgetBankOpen}
          onClose={() => setIsWidgetBankOpen(false)}
          widgetStates={widgetStates}
          onAddWidget={handleAddWidget}
          onToggleWidget={handleToggleWidget}
        />

        <CanvasViewportBinding store={viewportStore} propName="currentViewport">
        <RightSideMenu
          demoMode={demoMode}
          isWidgetBankOpen={isWidgetBankOpen}
          onWidgetBankToggle={() => setIsWidgetBankOpen(!isWidgetBankOpen)}
          widgetStates={widgetStates}
          onViewChange={handleViewChange}
          getWidgetPositionsAndSizes={getWidgetPositionsAndSizes}
          algorithms={algorithms}
          selectedAlgorithm={selectedAlgorithm}
          onAlgorithmChange={onAlgorithmChange}
          onRunAlgorithm={onRunAlgorithm}
          onStopAlgorithm={onStopAlgorithm}
          isRunningAlgorithm={isRunningAlgorithm}
          pipelineJob={pipelineJob}
          pipelineStatus={pipelineStatus}
          pipelineMessage={pipelineMessage}
          pipelineError={pipelineError}
          onOpenParameters={onOpenParameters}
          pipelineVariables={pipelineVariables}
          widgetInputBindings={widgetInputBindings}
          onWidgetBindingChange={handleWidgetBindingChange}
          displaySettings={displaySettings}
          onDisplaySettingsChange={handleDisplaySettingsChange}
          onResetDisplaySettings={handleResetDisplaySettings}
          customPipelines={customPipelines}
          isLoadingCustomPipelines={isLoadingCustomPipelines}
          customPipelineError={customPipelineError}
          onAddCustomPipeline={onAddCustomPipeline}
          onDeleteCustomPipeline={onDeleteCustomPipeline}
          canManageCustomPipelines={canManageCustomPipelines}
          savedViews={savedViews}
          savedCurrentViewId={savedCurrentViewId}
          onPersistViews={onPersistViews}
          layoutStorageScope={layoutStorageScope}
          onOpenChange={setIsRightSideMenuOpen}
        />
        </CanvasViewportBinding>

        <CanvasOverlayVisibility store={zoomVisibility} className="canvas-zoom-controls">
        <div
          aria-label="Canvas zoom controls"
          onMouseEnter={revealZoomIndicator}
        >
          <button type="button" onClick={() => setCanvasZoom(displaySettings.scale - ZOOM_STEP)} aria-label="Zoom out" disabled={displaySettings.scale <= ZOOM_MIN}>−</button>
          <button type="button" onClick={handleResetCanvasView} title="Reset canvas position and zoom">
            {Math.round(displaySettings.scale * 100)}%
          </button>
          <button type="button" onClick={() => setCanvasZoom(displaySettings.scale + ZOOM_STEP)} aria-label="Zoom in" disabled={displaySettings.scale >= ZOOM_MAX}>+</button>
        </div>

        </CanvasOverlayVisibility>

        {!isSideMenuOpen && (
          <CanvasOverlayVisibility store={minimapVisibility}>
          <CanvasViewportBinding store={viewportStore} geometry={canvasGeometry}>
          <CanvasMinimap
            widgets={canvasGeometry.widgets}
            onActivity={revealMinimap}
          />
          </CanvasViewportBinding>
          </CanvasOverlayVisibility>
        )}

        <CanvasCursorCoordinates
          ref={cursorCoordinatesRef}
          ariaLabel="Canvas cursor coordinates"
        />
      </div>

      {selectionBox && (
        <div
          className="widget-selection-box"
          aria-hidden="true"
          style={{
            left: selectionBox.left,
            top: selectionBox.top,
            width: selectionBox.width,
            height: selectionBox.height,
          }}
        />
      )}

      <div className="widget-selection-status" role="status" aria-live="polite">
        {selectedWidgetIds.length === 1
          ? '1 widget selected'
          : `${selectedWidgetIds.length} widgets selected`}
      </div>

      <CanvasViewportBinding store={viewportStore}><CanvasMarkup /></CanvasViewportBinding>

      <div
        className="dashboard-canvas"
        ref={dashboardCanvasRef}
        style={{
          transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${displaySettings.scale})`,
        }}
      >
        {canUseDashboardAssistant(widgetUser) && !demoMode && generatedWidgets.map(widget => (
          <React.Fragment key={widget.id}>
            {renderDockable(widget.id, widget.name, <GeneratedWidget widget={widget} data={aiWidgetData} />, 'generated-widget-panel')}
          </React.Fragment>
        ))}
        {!isDefaultView && isDragOver && dropPosition && (
          <div
            className="drop-indicator"
            style={{
              top: dropPosition.top,
              left: dropPosition.left
            }}
          >
            <span className="drop-indicator-icon">📥</span>
            <span>Drop widget here</span>
          </div>
        )}

      {renderDockable('clusterWaveform', 'Cluster Waveform Comparison',
        <ClusterWaveformWidget clusterData={clusterData} clusterWaveforms={clusterWaveforms}
          selectedClusters={selectedClusters} loadNeighbors={!demoMode && Number(datasetInfo?.totalChannels) > 0}
          dataCacheScope={dataCacheScope}
          highlightedSpike={highlightedSpikes.length ? {
            clusterId: highlightedSpikes[0].clusterId, spikeIndex: highlightedSpikes[0].pointIndex,
          } : null} />, 'panel-cluster-waveform')}
      {renderDockable('spikeWaveformGrid', 'In-cluster Spike Waveforms',
        <SpikeWaveformGrid dataset={curatorDataset} clusterData={clusterData}
          clusterWaveforms={clusterWaveforms} selectedClusters={selectedClusters}
          demoMode={useLocalClusterData} onSpikeSelect={handleSpikeHighlight} dataCacheScope={dataCacheScope} />,
        'panel-spike-waveform-grid')}
      {SOSSA_WIDGETS.map(({ id, name, component: Component }) => (
        <React.Fragment key={id}>{renderDockable(id, name,
          <Component dataset={curatorDataset} selectedClusters={selectedClusters} onSpikeSelect={handleSpikeHighlight}
            onOpenCurator={() => handleAddWidget(WIDGET_DEFINITIONS.curator)}
            onLoadExample={() => {
              handleCuratorDatasetChange(normalizeCuratorUpload(createSossaExample()));
              setSelectedClusters([]);
            }} />,
          'panel-sossa')}</React.Fragment>
      ))}
      {renderDockable(
        'clusterList',
        'Cluster Curation Table',
        <ClusterListTable
          clusters={clusters}
          selectedClusters={selectedClusters}
          clusterStats={clusterStats}
          clusterAnnotations={clusterAnnotations}
          groups={clusterGroupNames}
          onClusterSelect={handleClusterSelect}
          onAnnotationChange={handleAnnotationChange}
          onGroupsChange={handleClusterGroupsChange}
          onVisibleClustersChange={handleVisibleClusterOrderChange}
        />,
        'panel-cluster-list'
      )}
      {renderDockable(
  'amplitudeProfile',
  'Amplitude Distribution',
  <AmplitudeProfileWidget
    selectedClusters={selectedClusters}
    waveforms={clusterWaveforms}
    clusterData={clusterData}
    clusteringResults={curatorDataset ? null : clusteringResults}
    selectedAlgorithm={selectedAlgorithm}
    demoMode={demoMode}
    curatorMode={Boolean(curatorDataset)}
    onAmplitudeSummaries={handleAmplitudeSummaries}
    dataCacheScope={dataCacheScope}
  />,
  'panel-amplitude-profile'
)}

{renderDockable(
  'clusterComparison',
  'Cluster Comparison',
  <ClusterComparisonWidget
    selectedDataset={selectedDataset}
    clusteringResults={clusteringResults}
    selectedAlgorithm={selectedAlgorithm}
    selectedClusters={selectedClusters}
    demoMode={demoMode}
    onLoadingChange={handleWidgetLoadingChange}
  />,
  'panel-cluster-comparison'
)}

{renderDockable(
  'curator',
  'Curator',
  <CuratorWidget
    datasetInfo={datasetInfo}
    clusterAnnotations={clusterAnnotations}
    groups={clusterGroupNames}
    onAnnotationChange={handleAnnotationChange}
    initialDataset={curatorDataset}
    onDatasetChange={handleCuratorDatasetChange}
    selectedClusters={selectedClusters}
    onClusterSelect={handleCuratorClusterSelect}
    onSelectedClustersChange={handleCuratorSelectionChange}
    onLoadingChange={handleWidgetLoadingChange}
    sessionCacheScope={layoutStorageScope}
  />,
  'panel-curator'
)}

      {renderDockable(
        'curationActions',
        'Curation Actions',
        <CurationActionsWidget
          session={curationSession}
          currentClusterCount={curationDerivedState?.clusters?.length || clusters.length}
          selectedClusters={selectedClusters}
          selectedSpikes={curationSpikeSelection}
          error={curationActionError}
          recoveryMessage={curationRecoveryMessage}
          exportPayload={curationExportPayload}
          exportFilename={curationExportFilename}
          onMerge={handleMergeCuration}
          onSplit={handleSplitCuration}
          onUndo={handleUndoCuration}
          onRedo={handleRedoCuration}
          onReset={handleResetCuration}
          onClearSpikeSelection={() => handleCurationSpikeSelection([])}
        />,
        'panel-curation-actions'
      )}

{renderDockable(
  'rasterPlot',
  'Raster Plot',
  <RasterPlotWidget
    spikes={spikes}
    selectedClusters={selectedClusters}
    selectedAlgorithm={selectedAlgorithm}
    clusteringResults={clusteringResults}
    clusterData={clusterData}
    curatorDataset={curatorDataset}
    visibleClusterIds={visibleClusterFilterActive ? visibleClusterOrder : null}
    clusterOrder={visibleClusterOrder}
    highlightedSpikes={highlightedSpikes}
    linkedTimeRange={focusedTimeRange}
    onEventSelect={(event) => {
      handleSpikeHighlight(event);
      if (event?.spikeId) handleCurationSpikeSelection([event]);
    }}
    onClusterSelect={handleClusterSelect}
    demoMode={demoMode}
  />,
  'panel-raster-plot'
)}
      {renderDockable(
        'templateGallery',
        'Template Gallery',
        <TemplateGalleryWidget
          visibleClusterIds={populationClusterIds}
          selectedClusters={selectedClusters}
          clusterData={clusterData}
          clusterWaveforms={clusterWaveforms}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          onClusterSelect={handleClusterSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-template-gallery'
      )}
      {renderDockable(
        'clusterMetricScatter',
        'Cluster Metric Scatter',
        <ClusterMetricScatterWidget
          visibleClusterIds={populationClusterIds}
          selectedClusters={selectedClusters}
          clusters={clusters}
          clusterStats={clusterStats}
          clusterAnnotations={clusterAnnotations}
          onClusterSelect={handleClusterSelect}
        />,
        'panel-cluster-metric-scatter'
      )}
      {renderDockable(
        'correlogram',
        'Correlogram Matrix',
        <CorrelogramWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          spikes={spikes}
          clusterData={clusterData}
          clusteringResults={clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          onClusterSelect={handleClusterSelect}
          onClusterPairSelect={handleClusterPairSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-correlogram'
      )}

      {renderDockable(
        'similarityTable',
        'Similarity Table',
        <SimilarityTableWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          clusterData={clusterData}
          clusterWaveforms={clusterWaveforms}
          clusteringResults={clusteringResults}
          clusterStats={clusterStats}
          clusterAnnotations={clusterAnnotations}
          selectedAlgorithm={selectedAlgorithm}
          demoMode={useLocalClusterData}
          onClusterPairSelect={handleClusterPairSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-similarity-table'
      )}

      {renderDockable(
        'isiHistogram',
        'Inter-Spike Interval Histogram',
        <IsiHistogramWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          spikes={spikes}
          clusterData={clusterData}
          clusteringResults={clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          onClusterSelect={handleClusterSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-isi-histogram'
      )}

      {renderDockable(
        'amplitudeTime',
        'Amplitude vs Time / Drift',
        <AmplitudeTimeWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          spikes={spikes}
          clusterData={clusterData}
          clusterWaveforms={clusterWaveforms}
          clusteringResults={clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          highlightedSpikes={highlightedSpikes}
          linkedTimeRange={focusedTimeRange}
          onTimeRangeSelect={handleTimeRangeSelect}
          onSpikeSelect={handleSpikeHighlight}
          onSummaryChange={handleAmplitudeSummaries}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-amplitude-time'
      )}
      {renderDockable(
        'spikeAttributeExplorer',
        'Spike Attribute Explorer',
        <SpikeAttributeExplorerWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          clusterData={pcaClusterData}
          clusteringResults={curatorDataset ? null : clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          curationSpikeSelection={curationSpikeSelection}
          onCurationSelectionChange={handleCurationSpikeSelection}
          onSpikeSelect={handleSpikeHighlight}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-spike-attribute-explorer'
      )}
      {renderDockable(
        'featureMatrix',
        'Feature Matrix',
        <FeatureMatrixWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          selectedChannels={selectedChannels}
          clusterData={pcaClusterData}
          clusteringResults={curatorDataset ? null : clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          curationSpikeSelection={curationSpikeSelection}
          onCurationSelectionChange={handleCurationSpikeSelection}
          onSpikeSelect={handleSpikeHighlight}
          onSelectedChannelsChange={handleSelectedChannelsChange}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-feature-matrix'
      )}
      {renderDockable(
        'templateFeaturePair',
        'Template Feature Pair',
        <TemplateFeaturePairWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          selectedChannels={selectedChannels}
          clusterData={pcaClusterData}
          clusteringResults={curatorDataset ? null : clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          curationSpikeSelection={curationSpikeSelection}
          onCurationSelectionChange={handleCurationSpikeSelection}
          onSpikeSelect={handleSpikeHighlight}
          onClusterPairSelect={handleClusterPairSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-template-feature-pair'
      )}
      {renderDockable(
        'firingRateTimeline',
        'Firing Rate Timeline',
        <FiringRateTimelineWidget
          availableClusterIds={clusterData?.clusterIds || clusters.map((cluster) => cluster.id)}
          linkedSelectedClusters={selectedClusters}
          spikes={spikes}
          clusterData={clusterData}
          clusteringResults={clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          linkedTimeRange={focusedTimeRange}
          onTimeRangeSelect={handleTimeRangeSelect}
          onClusterSelect={handleClusterSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-firing-rate-timeline'
      )}
      {renderDockable(
        'probeMap',
        'Probe Map',
        <ProbeMapWidget
          selectedClusters={selectedClusters}
          selectedChannels={selectedChannels}
          clusterData={clusterData}
          selectedAlgorithm={selectedAlgorithm}
          datasetInfo={datasetInfo}
          demoMode={useLocalClusterData}
          onChannelSelect={handleChannelSelect}
          onSelectedChannelsChange={handleSelectedChannelsChange}
          onClusterSelect={handleClusterSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-probe-map'
      )}
      {renderDockable(
        'traceHeatmap',
        'Trace Heatmap',
        <TraceHeatmapWidget
          selectedChannels={selectedChannels}
          linkedTimeRange={focusedTimeRange}
          datasetInfo={datasetInfo}
          demoMode={demoMode}
          onChannelSelect={handleChannelSelect}
          onTimeRangeSelect={handleTimeRangeSelect}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-trace-heatmap'
      )}
      {renderDockable(
        'analysisWorkspace',
        'Analysis Workspace',
        <AnalysisWorkspaceWidget manifest={analysisManifest} />,
        'panel-analysis-workspace'
      )}
      {renderDockable(
        'spikeList',
        'Spike List Table',
        <SpikeListTable
          spikes={spikes}
          selectedSpike={selectedSpike}
          onSpikeSelect={(index, spike) => {
            setSelectedSpike(index);
            handleSpikeHighlight(spike);
            if (spike?.spikeId) handleCurationSpikeSelection([spike]);
          }}
          selectedClusters={selectedClusters}
        />,
        'panel-spike-list'
      )}

      {renderDockable(
        'clusterStats',
        'Cluster Statistics',
        <ClusterStatisticsWindow
          selectedClusters={selectedClusters}
          clusterStats={clusterStats}
        />,
        'panel-cluster-stats'
      )}

      {renderDockable(
        'signalView',
        'Signal View',
        <SignalViewPanel
          demoMode={demoMode}
          highlightedSpikes={highlightedSpikes}
          linkedTimeRange={focusedTimeRange}
          onTimeRangeChange={handleTimeRangeSelect}
          datasetInfo={datasetInfo}
          demoSignalData={demoSignalData}
          linkedSelectedChannels={selectedChannels}
          onSelectedChannelsChange={handleSelectedChannelsChange}
          clusterSpikes={spikes}
          dataCacheScope={dataCacheScope}
          onLoadingChange={handleWidgetLoadingChange}
        />,
        'panel-signal-view'
      )}

      {renderDockable(
        'dimReduction',
        'Dimensionality Reduction Plot View (PCA)',
        <DimensionalityReductionPanel
          clusterData={pcaClusterData}
          selectedClusters={selectedClusters}
          clusteringResults={curatorDataset ? null : clusteringResults}
          selectedAlgorithm={selectedAlgorithm}
          selectedSpike={highlightedSpikes.length > 0 ? {
            clusterId: highlightedSpikes[0].clusterId,
            pointIndex: highlightedSpikes[0].pointIndex
          } : null}
          onSpikeClick={handleSpikeHighlight}
          onSpikeSelectionChange={handleCurationSpikeSelection}
        />,
        'panel-dim-reduction'
      )}

      {renderDockable(
        'waveform',
        'Waveform View',
        <>
          <div className="waveform-view-toggle">
            <button
              className={waveformViewMode === 'single' ? 'active' : ''}
              onClick={() => setWaveformViewMode('single')}
            >
              Single Channel
            </button>
            <button
              className={waveformViewMode === 'neighboring' ? 'active' : ''}
              onClick={() => setWaveformViewMode('neighboring')}
            >
              Multi Channel
            </button>
          </div>

          {waveformViewMode === 'single' ? (
            <WaveformSingleChannelView
              selectedClusters={selectedClusters}
              clusterWaveforms={clusterWaveforms}
              highlightedSpike={highlightedSpikes.length > 0 ? {
                clusterId: highlightedSpikes[0].clusterId,
                spikeIndex: highlightedSpikes[0].pointIndex
              } : null}
            />
          ) : (
            <WaveformNeighboringChannelsView
              selectedClusters={selectedClusters}
              selectedAlgorithm={selectedAlgorithm}
              demoMode={demoMode}
              demoWaveforms={curationActive ? clusterWaveforms : demoWaveforms}
              clusterLookup={curatorDataset?.clusterLookup}
              dataCacheScope={dataCacheScope}
              onLoadingChange={handleWidgetLoadingChange}
              linkedSelectedChannels={selectedChannels}
              onChannelSelect={handleChannelSelect}
            />
          )}
        </>,
        'panel-waveform'
      )}
      </div>
    </div>
  );
});

MultiPanelView.displayName = 'MultiPanelView';
export default MultiPanelView;
